import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import {
  Plus, Loader2, CheckCircle, Users, UserPlus,
  X, FileText, Eye, ClipboardList,
  Download, ChevronRight, ChevronDown, Calendar, ClipboardCheck, Pencil,
  DollarSign, Search, Trash2, RotateCcw,
  CalendarDays, Wallet, Receipt, HandCoins, Ticket, FolderOpen, Clock, Timer,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import SortTh, { sortCompare, makeToggleSort } from '../components/SortTh';
import { payEntryHoursText } from '../lib/pay-entry-hours';
import { fmtCurrency, fmtHours, parseMoney, sumMoney, fmtPayType, PAY_TYPE_LABELS, PAYROLL_ENTRY_PAY_TYPES, isPercentagePayType } from '../lib/display-labels';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import DatePicker from '../components/DatePicker';
import ConfirmModal from '../components/ui/ConfirmModal';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { retryUnlessRefused, LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure, queryFailureError } from '../lib/load-failure';
import { api } from '../api/client';
import { filenameFromContentDisposition } from '../lib/content-disposition';
import { usePayrollPermissions } from '../hooks/usePayrollPermissions';
import {
  readPeriodClosed, reopenPayPeriod, reopenTargetFor, ReopenPeriodButton, ReopenPayPeriodConfirm, type PeriodClosed,
} from '../components/payroll/ReopenPayPeriod';
import { ADJUSTMENT_TYPES, ADJUSTMENT_TYPE_LABELS, getAdjustmentLabel } from '../lib/payroll-adjustments';
import { readPayStub, sharedValue, stubLineEarnings, stubLineHours, hoursTimesRate, shiftClockText, RATE_REASON_TEXT, type ClockShift, type PayGroup, type StubAdjustment } from '../lib/pay-stub-groups';
import ClockPayEntries, { hasHourlyClockPay, type ClockPayEntry } from '../components/payroll/ClockPayEntries';
import { RowError, rowErrorRingClass, groupRowErrors, type RowErrorReason } from '../components/ui/RowError';
import { calendarDayString, instantTenantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { LocaleContext, useTenantCurrency } from '../contexts/LocaleContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface PayPeriod {
  id: string;
  period_start: string;
  period_end: string;
  period_type: string;
  status: 'open' | 'approved' | 'exported' | 'paid';
  total_gross_pay: string | null;
  employee_count: number | null;
  approved_at: string | null;
  exported_at: string | null;
  created_at: string;
}

/**
 * GET /pay-periods/current, kept whole: the period the server calls current
 * (null on a 200 that carries none) and the tenant day it resolved that
 * against. `tenantToday` is null when an older API omits it.
 */
interface CurrentPeriodAnswer {
  period: PayPeriod | null;
  tenantToday: string | null;
}

interface TimesheetEntry {
  id: string;
  entry_date: string;
  ticket_number: string;
  customer_name: string;
  role_on_ticket: string;
  pay_type: string;
  percentage_applied: string;
  hours_worked: string;
  /**
   * Display only, never a pay input (api#836). The ticket's hours and its
   * calendar span; all three NULL on shop and manual entries. Render through
   * payEntryHoursText — a ticket-paid row with no ticket_hours is `—`, never
   * `hours_worked`.
   */
  ticket_hours?: string | number | null;
  ticket_work_date?: string | null;
  ticket_end_date?: string | null;
  base_amount: string;
  earnings: string;
  province_state: string;
  comments: string;
  /**
   * Set when this entry was priced from a clock-in shift rather than a ticket
   * -- the marker that makes an entry SHOP TIME. The column does not exist in
   * production yet (verified against the live catalog 2026-09-06: nineteen
   * columns, no such name), so this reads `undefined` on every row today and
   * the Shop time line reads $0.00 on every tenant. That is the correct
   * reading, not a gap: nothing writes shop pay into the ledger yet.
   *
   * The name is the API's own, from the shop-preview docblock in
   * payroll.routes.ts: "The landing place is a `timesheet_entries` row keyed
   * by a `source_timesheet_id` column that is still owed; no separate table."
   * The detail route selects `te.*`, so the field arrives here the day the
   * column lands, with no frontend change.
   *
   * WHY NOT A CHEAPER TEST. `pay_type === 'hourly'` and `ticket_id IS NULL`
   * both look like they would identify shop work and both are wrong TODAY:
   * production holds one manual hourly entry with no ticket carrying $2,115.75
   * on a live tenant (TNT, 2026-08-01). Either test would move that money out
   * of Ticket earnings and file it under Shop time, which is the invented
   * value this line exists to avoid.
   */
  source_timesheet_id?: string | null;
}

interface Timesheet {
  id: string;
  user_id: string;
  status: string;
  total_earnings: string | null;
  /**
   * The overtime premium on this worker's timesheet. Not in total_earnings,
   * which is the entries' sum — so Gross pay that read only total_earnings +
   * adjustments came out short by every premium (Lone Star Oct 1–15: $7,855
   * against a stored $7,925). Nullable column, default 0; NULL is absent.
   */
  overtime_earnings?: string | null;
  first_name: string;
  last_name: string;
  initials: string;
  email: string;
  entries: TimesheetEntry[];
}

interface Adjustment {
  id: string;
  user_id: string;
  adjustment_type: string;
  description: string;
  amount: string;
  first_name: string;
  last_name: string;
  initials: string;
  created_at: string;
  /**
   * The approved expense this adjustment was created FROM. Written by
   * `POST /expenses/:id/approve`, which inserts a `payroll_adjustments` row of
   * type `reimbursement` carrying the gross amount. `GET
   * /pay-periods/:id/adjustments` selects `pa.*`, so it has always been on this
   * payload -- the field was undeclared, not absent.
   *
   * PROVENANCE, NOT TYPE, is what the Expenses line filters on. The office can
   * also type a `reimbursement` adjustment by hand in the adjustment modal, and
   * that is not an expense; it has no receipt, no approver and no expense
   * record behind it. Those fall to Other adjustments, where they are still
   * shown and still counted.
   */
  source_expense_id?: string | null;
}

interface PayPeriodDetail extends PayPeriod {
  timesheets: Timesheet[];
  /** companies.province_state, two letters; shown US-only where a worker's entries carry none. */
  companyRegion?: string | null;
  company_region?: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// Parse date string as UTC to avoid timezone shift (e.g. 2026-03-15T00:00:00Z showing as Mar 14 in MST)
function parseUTC(d: string): Date {
  const dateStr = d.slice(0, 10); // extract YYYY-MM-DD
  const [y, m, day] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, day); // treat as local date (no timezone shift)
}

function formatDateRange(start: string, end: string) {
  try {
    const s = parseUTC(start);
    const e = parseUTC(end);
    const sameYear = s.getFullYear() === e.getFullYear();
    const sameMonth = sameYear && s.getMonth() === e.getMonth();
    if (sameMonth) {
      return `${format(s, 'MMM d')}-${format(e, 'd, yyyy')}`;
    }
    return `${format(s, 'MMM d')} - ${format(e, 'MMM d, yyyy')}`;
  } catch { return `${start} - ${end}`; }
}

function formatDateShort(d: string) {
  try { return format(parseUTC(d), 'MMM d'); } catch { return d; }
}

/**
 * approved_at / exported_at are INSTANTS: the TENANT's day they fell on, as
 * 'yyyy-MM-dd'. Slicing the UTC prefix dated an evening approval in Alberta a
 * day late. An em dash when the stamp or the zone is unusable.
 */
function formatDateFull(d: string, timeZone: string | null) {
  return instantTenantDay(d, timeZone) ?? '—';
}

/** @see fmtCurrency — absence renders '—', not '$0.00'. */
const money = (v: string | number | null | undefined) => fmtCurrency(v);

function getInitials(first: string, last: string, stored?: string) {
  if (stored) return stored;
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
}


/**
 * The register's status pill and row rail — the Quotes / Rate Sheets badge,
 * dark ink on a saturated signal fill. EXHAUSTIVE over `pay_period_status`,
 * which is FOUR values and only four: the enum is
 * `('open','approved','exported','paid')` (production-schema.sql). `locked` and
 * `pending` are not members — the routes that used to write them were removed
 * after they threw 22P02 — so nothing else can arrive here from the API.
 *
 * FOUR STATUSES, THREE HUES, and the sharing is by meaning rather than by
 * running out of colours. `approved` and `exported` both mean SIGNED OFF and
 * take green; the pill's label is what tells them apart, and an export is a
 * delivery of an approved period rather than a different state of the money.
 * `open` is amber because it is the one that still wants work. `paid` takes
 * blue — the terminal, money-has-gone-out state — and deliberately NOT slate,
 * which reads as inactive or dead on every other register in the app. A paid
 * period is the successful end of the cycle, not a retired row.
 *
 * THIS REPLACES THREE HAND-ROLLED PALETTES that had drifted apart on one
 * screen. The mobile card's copy had no `exported` and no `paid` case at all,
 * so a PAID period fell to its grey default and rendered identically to an
 * exported one — on the single screen whose whole question is "did the money
 * go out?". The card stack is gone with this reskin and the map is now the one
 * source for the badge, the rail and the current-period card.
 *
 * A value outside the enum reads `—` on slate with a transparent rail — never
 * a guessed label, and never a fall back to `open`, which is what the old
 * `STATUS_CONFIG[status] || STATUS_CONFIG.open` did. On a payroll register
 * "open" is a claim that the period is still editable and that nobody has been
 * paid, which is the most consequential thing this screen can say wrongly.
 */
const PERIOD_STATUS: Record<PayPeriod['status'], { label: string; fill: string; rail: string; dot: string }> = {
  open:     { label: 'OPEN',     fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber', dot: 'bg-fi-signal-amber' },
  approved: { label: 'APPROVED', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green', dot: 'bg-fi-signal-green' },
  exported: { label: 'EXPORTED', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green', dot: 'bg-fi-signal-green' },
  paid:     { label: 'PAID',     fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue',  dot: 'bg-fi-signal-blue' },
};

const UNKNOWN_PERIOD_STATUS = {
  label: '—',
  fill: 'bg-fi-signal-slate',
  rail: 'border-l-transparent',
  dot: 'bg-fi-signal-slate',
};

const periodSkin = (status: string | null | undefined) =>
  (status && PERIOD_STATUS[status as PayPeriod['status']]) || UNKNOWN_PERIOD_STATUS;

/**
 * The pay-period filter tabs, and the source of `PayPeriodFilterTab`.
 *
 * At module scope so the state's type is DERIVED from this list rather than
 * hand-maintained beside it. It was a second copy before, and adding 'Paid'
 * compiled against the list while failing against the copy -- which is the
 * cheap version of this bug. The expensive version is the copy silently
 * winning.
 *
 * 'All' aside, each tab lowercases to a pay_period_status label; the filter
 * below relies on that.
 */
/**
 * The styling for "this cannot be undone", shared by every irreversible-action
 * confirmation on this screen.
 *
 * It is a CONSTANT rather than a copied class string because the two modals
 * using it drifted the first time: force-delete rendered its warning in red
 * while mark-as-paid rendered the same sentence in bold white. Both sat above
 * a red confirm button so neither was unmarked, and nothing caught it —
 * no test asserts a colour, and both renders look deliberate.
 *
 * That is the silent-blank-field family applied to emphasis: the failure mode
 * is not a crash or a gap, it is two confident answers to one question. The
 * fix that survives is the one that makes divergence impossible rather than
 * merely correcting today's instance.
 */
export const IRREVERSIBLE_WARNING_CLASS = 'text-red-400 font-medium';

const PAY_PERIOD_FILTER_TABS = ['All', 'Open', 'Approved', 'Exported', 'Paid'] as const;
type PayPeriodFilterTab = typeof PAY_PERIOD_FILTER_TABS[number];

/**
 * The status pill. One shape for the band, the register row and the
 * current-period card, so the three cannot drift the way the old trio did.
 *
 * The signal hues do not follow the theme, so one dark ink holds on all of
 * them in both halves of the palette.
 */
function StatusBadge({ status }: { status: string | null | undefined }) {
  const skin = periodSkin(status);
  return (
    <span
      data-testid="pay-period-status"
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

// ─── New Period Modal ───────────────────────────────────────────────────────

function NewPeriodModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [periodType, setPeriodType] = useState('semi_monthly');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!startDate || !endDate) { setError('Start and end dates are required'); return; }
    setSaving(true);
    setError('');
    try {
      await api.payroll.createPayPeriod({ periodStart: startDate, periodEnd: endDate, periodType });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create period');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">New Pay Period</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white"><X size={20} /></button>
        </div>
        {error && <div className="mb-3 text-sm text-fieldiq-danger bg-fieldiq-danger/10 rounded-lg p-3">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Start Date</label>
            <DatePicker value={startDate} onChange={setStartDate} placeholder="Select start date" />
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">End Date</label>
            <DatePicker value={endDate} onChange={setEndDate} placeholder="Select end date" />
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Frequency</label>
            <select value={periodType} onChange={e => setPeriodType(e.target.value)}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm">
              <option value="weekly">Weekly</option>
              <option value="bi_weekly">Bi-Weekly</option>
              <option value="semi_monthly">Bi-Monthly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-fieldiq-border rounded-lg text-fieldiq-text text-sm hover:text-white">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 px-4 py-2 bg-fieldiq-accent text-black rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50">
            {saving ? 'Creating...' : 'Create Period'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Adjustment Modal ───────────────────────────────────────────────────────

function AdjustmentModal({ periodId, timesheets, onClose, onCreated, onPeriodClosed }: {
  periodId: string; timesheets: Timesheet[]; onClose: () => void; onCreated: () => void;
  /** The period closed under this modal. The detail view owns the message and the way out. */
  onPeriodClosed: (closed: PeriodClosed) => void;
}) {
  const [userId, setUserId] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('bonus');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!userId || !description || !amount) { setError('All fields are required'); return; }
    setSaving(true);
    setError('');
    try {
      await api.payroll.addAdjustment(periodId, { userId, adjustmentType, description, amount: parseFloat(amount) });
      onCreated();
      onClose();
    } catch (err: any) {
      // Handed up rather than shown here: the Reopen control lives on the
      // detail header, and an error inside a modal cannot point at it.
      const closed = readPeriodClosed(err);
      if (closed) { onPeriodClosed(closed); onClose(); return; }
      setError(err.response?.data?.error || 'Failed to add adjustment');
    } finally { setSaving(false); }
  };

  const cls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-fieldiq-muted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Add Adjustment</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white"><X size={20} /></button>
        </div>
        {error && <div className="mb-3 text-sm text-fieldiq-danger bg-fieldiq-danger/10 rounded-lg p-3">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Employee</label>
            <select value={userId} onChange={e => setUserId(e.target.value)} className={cls}>
              <option value="">Select employee...</option>
              {timesheets.map(ts => <option key={ts.user_id} value={ts.user_id}>{ts.first_name} {ts.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Type</label>
            <select value={adjustmentType} onChange={e => setAdjustmentType(e.target.value)} className={cls}>
              {ADJUSTMENT_TYPES.map(t => (
                <option key={t} value={t}>{ADJUSTMENT_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Safety bonus" className={cls} />
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Amount (negative for deductions)</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="100.00" className={cls} />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-fieldiq-border rounded-lg text-fieldiq-text text-sm hover:text-white">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 px-4 py-2 bg-fieldiq-accent text-black rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50">
            {saving ? 'Saving...' : 'Add Adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Employee Modal ─────────────────────────────────────────────────────

function AddEmployeeModal({ periodId, onClose, onCreated, onPeriodClosed }: {
  periodId: string; onClose: () => void; onCreated: () => void;
  /** Same handoff as AdjustmentModal's: the detail view owns the message and the way out. */
  onPeriodClosed: (closed: PeriodClosed) => void;
}) {
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userId, setUserId] = useState('');
  const [payType, setPayType] = useState('hourly');
  const [role, setRole] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [days, setDays] = useState('1');
  const [dayRate, setDayRate] = useState('');
  const [baseAmount, setBaseAmount] = useState('');
  const [percentage, setPercentage] = useState('');
  const [flatAmount, setFlatAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.payroll.availableEmployees(periodId).then(res => setUsers(res.data.data || []))
      .catch(() => {}).finally(() => setLoadingUsers(false));
  }, [periodId]);

  const selectedUser = users.find((u: any) => u.id === userId);

  useEffect(() => {
    if (!selectedUser?.pay_rates?.length) return;
    const first = selectedUser.pay_rates[0];
    if (first.hourly_rate_amount) { setPayType('hourly'); setHourlyRate(String(Number(first.hourly_rate_amount))); }
    else if (first.day_rate_amount) { setPayType('day_rate'); setDayRate(String(Number(first.day_rate_amount))); }
    else if (first.single_tech_percentage) { setPayType('percentage_single'); setPercentage(String(Math.round(Number(first.single_tech_percentage) * 10000) / 100)); }
    else if (first.dual_tech_percentage) { setPayType('percentage_dual'); setPercentage(String(Math.round(Number(first.dual_tech_percentage) * 10000) / 100)); }
    else if (first.triple_tech_percentage) { setPayType('percentage_triple'); setPercentage(String(Math.round(Number(first.triple_tech_percentage) * 10000) / 100)); }
    else if (first.quad_tech_percentage) { setPayType('percentage_quad'); setPercentage(String(Math.round(Number(first.quad_tech_percentage) * 10000) / 100)); }
  }, [selectedUser]);

  const computedTotal = (() => {
    switch (payType) {
      case 'hourly': return (parseFloat(hours) || 0) * (parseFloat(hourlyRate) || 0);
      case 'day_rate': return (parseFloat(days) || 0) * (parseFloat(dayRate) || 0);
      case 'percentage_single': case 'percentage_dual': case 'percentage_triple': case 'percentage_quad': return (parseFloat(baseAmount) || 0) * ((parseFloat(percentage) || 0) / 100);
      case 'bonus': case 'stat_holiday': case 'other': return parseFloat(flatAmount) || 0;
      default: return 0;
    }
  })();

  const handleSubmit = async () => {
    if (!userId) { setError('Select an employee'); return; }
    if (computedTotal <= 0) { setError('Enter pay details — calculated pay is $0'); return; }
    setSaving(true); setError('');
    try {
      const payload: any = { userId, payType, role: role || undefined, description: description || undefined };
      switch (payType) {
        case 'hourly': payload.hours = parseFloat(hours) || 0; payload.rate = parseFloat(hourlyRate) || 0; break;
        case 'day_rate': payload.days = parseFloat(days) || 1; payload.dayRate = parseFloat(dayRate) || 0; break;
        case 'percentage_single': case 'percentage_dual': case 'percentage_triple': case 'percentage_quad': payload.baseAmount = parseFloat(baseAmount) || 0; payload.percentage = (parseFloat(percentage) || 0) / 100; break;
        case 'bonus': case 'stat_holiday': case 'other': payload.amount = parseFloat(flatAmount) || 0; break;
      }
      await api.payroll.addEmployee(periodId, payload);
      onCreated(); onClose();
    } catch (err: any) {
      const closed = readPeriodClosed(err);
      if (closed) { onPeriodClosed(closed); onClose(); return; }
      setError(err.response?.data?.error || 'Failed to add employee');
    } finally { setSaving(false); }
  };

  const cls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-fieldiq-muted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Add Employee</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white"><X size={20} /></button>
        </div>
        {error && <div className="mb-3 text-sm text-fieldiq-danger bg-fieldiq-danger/10 rounded-lg p-3">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Team Member</label>
            {loadingUsers ? <div className="flex items-center gap-2 text-fieldiq-text text-sm py-2"><Loader2 size={14} className="animate-spin" /> Loading team...</div>
             : users.length === 0 ? <p className="text-sm text-fieldiq-muted py-2">All team members already added</p>
             : <select value={userId} onChange={e => setUserId(e.target.value)} className={cls}><option value="">Select employee...</option>
                {users.map((u: any) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.role || 'Employee'}</option>)}
              </select>}
          </div>
          {selectedUser?.pay_rates?.length > 0 && (
            <div className="bg-fieldiq-dark/50 rounded-lg p-3 border border-fieldiq-border">
              <p className="text-sm text-fieldiq-text uppercase tracking-wide mb-1.5">Configured Pay Rates</p>
              {selectedUser.pay_rates.map((pr: any, i: number) => (
                <div key={i} className="text-sm text-fieldiq-text flex justify-between">
                  <span className="capitalize">{(pr.line_item_type || '').replace(/_/g, ' ')}</span>
                  <span className="text-white">
                    {pr.hourly_rate_amount != null && fmtCurrency(pr.hourly_rate_amount, null, 2, '/hr')}
                    {pr.day_rate_amount != null && fmtCurrency(pr.day_rate_amount, null, 2, '/day')}
                    {pr.single_tech_percentage && `${(Number(pr.single_tech_percentage) * 100).toFixed(1)}% (single)`}
                    {pr.dual_tech_percentage && `${(Number(pr.dual_tech_percentage) * 100).toFixed(1)}% (dual)`}
                    {pr.triple_tech_percentage && `${(Number(pr.triple_tech_percentage) * 100).toFixed(1)}% (triple)`}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Pay Type</label>
            <select value={payType} onChange={e => setPayType(e.target.value)} className={cls}>
              {PAYROLL_ENTRY_PAY_TYPES.map(v => <option key={v} value={v}>{PAY_TYPE_LABELS[v]}</option>)}
            </select>
          </div>
          {payType === 'hourly' && <div className="grid grid-cols-2 gap-3"><div><label className="block text-sm text-fieldiq-text mb-1">Hours</label><input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="80" className={cls} /></div><div><label className="block text-sm text-fieldiq-text mb-1">Rate ($/hr)</label><input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="55.00" className={cls} /></div></div>}
          {payType === 'day_rate' && <div className="grid grid-cols-2 gap-3"><div><label className="block text-sm text-fieldiq-text mb-1">Days</label><input type="number" step="1" value={days} onChange={e => setDays(e.target.value)} placeholder="1" className={cls} /></div><div><label className="block text-sm text-fieldiq-text mb-1">Day Rate ($)</label><input type="number" step="0.01" value={dayRate} onChange={e => setDayRate(e.target.value)} placeholder="650.00" className={cls} /></div></div>}
          {isPercentagePayType(payType) && <div className="grid grid-cols-2 gap-3"><div><label className="block text-sm text-fieldiq-text mb-1">Base Amount ($)</label><input type="number" step="0.01" value={baseAmount} onChange={e => setBaseAmount(e.target.value)} placeholder="5000.00" className={cls} /></div><div><label className="block text-sm text-fieldiq-text mb-1">Percentage (%)</label><input type="number" step="0.1" value={percentage} onChange={e => setPercentage(e.target.value)} placeholder="25" className={cls} /></div></div>}
          {(payType === 'bonus' || payType === 'stat_holiday' || payType === 'other') && <div><label className="block text-sm text-fieldiq-text mb-1">Amount ($)</label><input type="number" step="0.01" value={flatAmount} onChange={e => setFlatAmount(e.target.value)} placeholder="500.00" className={cls} /></div>}
          <div className={`bg-fieldiq-dark rounded-lg px-3 py-2.5 flex items-center justify-between border ${computedTotal > 0 ? 'border-fieldiq-accent/30' : 'border-fieldiq-border'}`}>
            <span className="text-sm text-fieldiq-text">Calculated Pay</span>
            <span className={`font-bold text-lg ${computedTotal > 0 ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}>{money(computedTotal)}</span>
          </div>
          <div><label className="block text-sm text-fieldiq-text mb-1">Role / Position</label><input type="text" value={role} onChange={e => setRole(e.target.value)} placeholder={selectedUser?.role || 'e.g. Lead Hand'} className={cls} /></div>
          <div><label className="block text-sm text-fieldiq-text mb-1">Notes (optional)</label><input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Manual entry for this period" className={cls} /></div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-fieldiq-border rounded-lg text-fieldiq-text text-sm hover:text-white">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !userId}
            className="flex-1 px-4 py-2.5 bg-fieldiq-accent text-black rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50">
            {saving ? 'Adding...' : 'Add Employee'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Stat holiday pay ───────────────────────────────────────────────────────
//
// Stat pay is a `stat_holiday` payroll adjustment, written through the same
// adjustments routes the period view uses (POST to add, PATCH to edit since
// api#1127, DELETE to remove — admin only, the API decides). The Stat holiday
// line in the summary is the API's figure: these adjustments PLUS any ticket
// line the server classed as stat. Only the adjustments are editable here; a
// ticket line belongs to its ticket.

function StatHolidayModal({ periodId, userId, empName, statAdjustments, onClose, onSaved, onPeriodClosed }: {
  periodId: string;
  userId: string;
  empName: string;
  statAdjustments: StubAdjustment[];
  onClose: () => void;
  onSaved: () => void;
  onPeriodClosed: (closed: PeriodClosed) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, { description: string; amount: string }>>(() =>
    Object.fromEntries(statAdjustments.map(a => [a.id, {
      description: a.description ?? '',
      amount: a.amount == null ? '' : String(a.amount),
    }]))
  );
  const [newDescription, setNewDescription] = useState('Stat holiday');
  const [newAmount, setNewAmount] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const fail = (err: any, fallback: string) => {
    const closed = readPeriodClosed(err);
    if (closed) { onPeriodClosed(closed); return; }
    setError(err?.response?.data?.error || fallback);
  };

  const parseAmount = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const save = async (id: string) => {
    const d = drafts[id];
    const amount = parseAmount(d.amount);
    if (amount === null || !d.description.trim()) { setError('A description and an amount are required.'); return; }
    setBusy(id); setError('');
    try {
      await api.payroll.updateAdjustment(periodId, id, { description: d.description.trim(), amount });
      onSaved();
      onClose();
    } catch (err: any) { fail(err, 'Could not save the stat pay.'); } finally { setBusy(null); }
  };

  const remove = async (id: string) => {
    setBusy(id); setError('');
    try {
      await api.payroll.deleteAdjustment(periodId, id);
      onSaved();
      onClose();
    } catch (err: any) { fail(err, 'Could not remove the stat pay.'); } finally { setBusy(null); }
  };

  const add = async () => {
    const amount = parseAmount(newAmount);
    if (amount === null || !newDescription.trim()) { setError('A description and an amount are required.'); return; }
    setBusy('new'); setError('');
    try {
      await api.payroll.addAdjustment(periodId, {
        userId, adjustmentType: 'stat_holiday', description: newDescription.trim(), amount,
      });
      onSaved();
      onClose();
    } catch (err: any) { fail(err, 'Could not add the stat pay.'); } finally { setBusy(null); }
  };

  const cls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-fieldiq-text text-sm placeholder:text-fieldiq-muted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="stat-pay-title"
        className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 id="stat-pay-title" className="text-lg font-bold text-fieldiq-text">Stat holiday pay</h3>
          <button onClick={onClose} aria-label="Close" className="text-fieldiq-text hover:text-fieldiq-muted"><X size={20} /></button>
        </div>
        <p className="mb-4 text-xs text-fieldiq-muted">{empName}, this pay period. Stat lines on tickets are counted too, and are changed on the ticket.</p>
        {error && <div role="alert" className="mb-3 text-sm text-fieldiq-text bg-fieldiq-danger/10 border border-fieldiq-danger/40 rounded-lg p-3">{error}</div>}

        {statAdjustments.length > 0 && (
          <div className="space-y-3 mb-5">
            {statAdjustments.map(a => (
              <div key={a.id} className="grid grid-cols-[1fr_7rem] gap-2 items-end">
                <label className="block text-xs text-fieldiq-muted">Description
                  <input type="text" value={drafts[a.id]?.description ?? ''} className={cls}
                    onChange={e => setDrafts(p => ({ ...p, [a.id]: { ...p[a.id], description: e.target.value } }))} />
                </label>
                <label className="block text-xs text-fieldiq-muted">Amount ($)
                  <input type="number" step="0.01" value={drafts[a.id]?.amount ?? ''} className={cls}
                    onChange={e => setDrafts(p => ({ ...p, [a.id]: { ...p[a.id], amount: e.target.value } }))} />
                </label>
                <div className="col-span-2 flex justify-end gap-2">
                  <button type="button" onClick={() => remove(a.id)} disabled={busy !== null}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-fieldiq-border rounded-lg text-xs text-fieldiq-text hover:bg-fieldiq-dark disabled:opacity-50">
                    <Trash2 size={12} aria-hidden="true" /> Remove
                  </button>
                  <button type="button" onClick={() => save(a.id)} disabled={busy !== null}
                    className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
                    {busy === a.id ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-fieldiq-border pt-4">
          <p className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Add stat pay</p>
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <label className="block text-xs text-fieldiq-muted">Description
              <input type="text" value={newDescription} onChange={e => setNewDescription(e.target.value)} className={cls} />
            </label>
            <label className="block text-xs text-fieldiq-muted">Amount ($)
              <input type="number" step="0.01" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="400.00" className={cls} />
            </label>
          </div>
          <div className="flex gap-3 mt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-fieldiq-card border border-fieldiq-border rounded-lg text-fieldiq-text text-sm hover:bg-fieldiq-dark">Cancel</button>
            <button type="button" onClick={add} disabled={busy !== null}
              className="flex-1 fi-action fi-action--standard fi-action--primary justify-center disabled:opacity-50">
              {busy === 'new' ? 'Adding…' : 'Add stat pay'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Employee Detail View (Pay Stub) ────────────────────────────────────────

function EmployeeDetailView({ periodId, ts, adjustments, status, periodStart, periodEnd, onBack, onRefresh, onPeriodClosed }: {
  periodId: string;
  ts: Timesheet;
  adjustments: Adjustment[];
  status: string;
  periodStart: string;
  periodEnd: string;
  onBack: () => void;
  onRefresh: () => void;
  /**
   * A write from this view was refused because the period closed. This view
   * has no error band and no Reopen confirmation, so the refusal goes back to
   * the period view, which has both.
   */
  onPeriodClosed: (closed: PeriodClosed) => void;
}) {
  // The tenant's currency (companies.currency), null until the server has said
  // — never a guessed 'CAD'. These labels were the literal "CAD", so every US
  // tenant's pay read as Canadian dollars. A null code is omitted, not guessed.
  const currency = useTenantCurrency();
  // Wording only: a US tenant's region is a State. Canada reads as before.
  const usTenant = useContext(LocaleContext)?.isAmerican === true;
  const [showAddEmpModal, setShowAddEmpModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const empName = `${ts.first_name} ${ts.last_name}`;

  // Close export menu on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showExportMenu]);

  const handleExportCSV = () => {
    setShowExportMenu(false);
    const period = formatDateRange(periodStart, periodEnd);
    const headers = ['Date', 'Ticket #', 'Company', 'Role', 'Hours', 'Pay Type', 'Earnings', usTenant ? 'State' : 'Province', 'Comments'];
    const rows = ts.entries.map(e => [
      e.entry_date ? formatDateShort(e.entry_date) : '',
      e.ticket_number || '', e.customer_name || '', e.role_on_ticket || '',
      // A clock shift exports as hours × rate = amount, from the stub's shift.
      (() => { const sh = shiftsById.get(String(e.id)); return (sh && hoursTimesRate(sh.hours, sh.rate, sh.amount, money)) || payEntryHoursText(e); })(),
      fmtPayType(e.pay_type, e.percentage_applied),
      // parseMoney, not `|| 0`: an absent figure exports as an em dash, never 0.00.
      parseMoney(e.earnings)?.toFixed(2) ?? '—', e.province_state || '', e.comments || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${empName.replace(/\s/g, '_')}_${period.replace(/[^a-zA-Z0-9]/g, '_')}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * The grouped lines, the summary lines and the total all come from the pay
   * stub route (api#1127) -- `lib/pay-stub-groups.ts` says why none of them is
   * summed here. The period payload this view is handed (`ts`) still feeds the
   * CSV and the register's Total row, which are unchanged.
   */
  const queryClient = useQueryClient();
  const stubKey = ['payroll', 'pay-stub', periodId, ts.user_id];
  const stubQuery = useQuery({
    queryKey: stubKey,
    queryFn: async () => readPayStub((await api.payroll.getPayStub(periodId, ts.user_id)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const stub = stubQuery.data;
  const stubFailed = queryLoadFailure(stubQuery) !== null;
  const stubLoading = !stub && !stubFailed;
  const lines = stub?.lines ?? null;
  const groups = stub?.groups ?? null;
  const [showStatModal, setShowStatModal] = useState(false);
  const refreshStub = () => {
    queryClient.invalidateQueries({ queryKey: stubKey });
    onRefresh();
  };

  // One group open at a time is not a rule here: the office compares tickets.
  // Every group starts shut -- the payroll reader asked for the consolidated
  // view, and the lines under a group are the detail he opens on demand.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const toggleGroup = (key: string) => setOpenGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const entriesById = new Map<string, any>((stub?.entries ?? []).map(e => [String(e.id), e]));
  const groupEntries = (g: PayGroup) => g.entryIds.map(id => entriesById.get(id)).filter(Boolean) as any[];
  const timeZone = useTenantTimeZone();
  /**
   * A clock group's (shop / hourly field) figure: "14.75 hrs × $25.00 =
   * $368.75", every number the API's (api#1193). null on a ticket group, or
   * when the API sent no hours, and the caller keeps the line's own reading.
   */
  const clockGroupText = (g: PayGroup) =>
    g.category === 'shop' || g.category === 'hourly_field' ? hoursTimesRate(g.hours, g.rate, g.amount, money) : null;
  const shiftsById = new Map<string, ClockShift>(
    (groups ?? []).flatMap(g => g.shifts ?? []).map(sh => [sh.entryId, sh]),
  );

  const handleExportPDF = () => {
    setShowExportMenu(false);
    // Grouped rows only -- the lines under each group stay on screen. The
    // total is the API's `summary.lines.total`; the menu item is disabled
    // until the stub has loaded, so there is no fallback figure to print.
    if (!groups || !lines) return;
    const period = formatDateRange(periodStart, periodEnd);
    const esc = (v: string | null | undefined) =>
      String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const stubAdjs = (stub?.adjustments ?? []);
    // Same columns as before. Role and Hours print only when every line in
    // the group shares them -- the screen's rule, so print and screen agree.
    const rows = groups.map(g => {
      const members = groupEntries(g);
      return `<tr><td>${g.date ? formatDateShort(g.date) : '—'}</td><td>${esc(g.ticketNumber) || '—'}</td><td>${esc(g.customerName) || '—'}</td><td>${esc(sharedValue(members, e => e.role_on_ticket)) || '—'}</td><td>${esc(clockGroupText(g) ?? sharedValue(members, e => payEntryHoursText(e))) || '—'}</td><td>${esc(g.label) || '—'}</td><td style="text-align:right">${money(g.amount)}</td></tr>`;
    }).join('') + stubAdjs.map(a =>
      `<tr><td></td><td></td><td></td><td></td><td></td><td>${esc(getAdjustmentLabel(a.adjustment_type ?? ''))}${a.description ? ` — ${esc(a.description)}` : ''}</td><td style="text-align:right">${money(a.amount)}</td></tr>`
    ).join('');
    const html = `<html><head><title>Pay Stub - ${esc(empName)}</title><style>body{font-family:Arial;margin:30px;font-size:13px}table{width:100%;border-collapse:collapse;margin:20px 0}th{background:#1a365d;color:#fff;text-align:left;padding:8px}td{padding:6px 8px;border-bottom:1px solid #e2e8f0}.total{font-weight:bold;font-size:16px}</style></head><body><h1>${esc(empName)}</h1><p>Pay Period: ${period}</p><h2 style="color:#d4943a">${money(lines.total)}</h2><table><tr><th>Date</th><th>Ticket</th><th>Company</th><th>Role</th><th>Hours</th><th>Pay Type</th><th style="text-align:right">Earnings</th></tr>${rows}<tr style="background:#f7fafc"><td colspan="6" style="font-weight:bold">TOTAL</td><td style="text-align:right;font-weight:bold">${money(lines.total)}</td></tr></table><p style="color:#999;font-size:11px">Generated by FieldIQ</p></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.print(); }
  };
  // The register's Total row: the period payload's entry sum, as before.
  const earnings = Number(ts.total_earnings || 0);
  const userAdjs = adjustments.filter(a => a.user_id === ts.user_id);
  const statAdjs = (stub?.adjustments ?? []).filter(a => a.adjustment_type === 'stat_holiday');
  const hasHourlyFieldTime = (stub?.entries ?? []).some(e => (e.pay_category ?? e.payCategory) === 'hourly_field');
  /**
   * A US hourly worker, by the API's `isHourly` -- never inferred here from
   * country or from clock-in rows. Only then does the stub read in hours, put
   * the Overtime line and the adjustments in the register, and total the
   * register to the API's `lines.total`. An API without the key keeps the
   * ticket layout: its group amounts are pre-split straight time, and an
   * Overtime row beside them would count those hours twice.
   */
  const hourly = stub?.isHourly === true;
  // The band's SKELETON before the stub arrives. Which layout is right is the
  // API's answer, and until it lands the likelier one is the tenant's: a US
  // tenant's stub drew Ticket pay / Tickets for a beat before swapping to hours.
  // Only the labels are guessed; every figure is a pulse until the stub says.
  const hourlyBand = hourly || (stubLoading && usTenant);
  // A shop row has no work site, so the API leaves its region NULL. On a US
  // tenant that row is worked in the company's own state (the same home state
  // the hourly engine uses). Canada keeps its dash.
  const regionOf = (v: string | null | undefined) => v || (usTenant ? stub?.companyRegion : null) || null;
  const regionLabel = usTenant ? 'State' : 'Prov';

  // Find primary province from entries
  const provinces = ts.entries.map(e => e.province_state).filter(Boolean);
  const primaryProvince = provinces.length > 0 ? provinces[0] : (regionOf(null) ?? '—');

  /**
   * The register's reference column for a clock-in row: the clock-in's job,
   * plus the ticket on that job for that day when there is one. A shop row has
   * neither and says so. Ticket-paid rows keep their ticket number.
   */
  const payRef = (row: { category?: string | null; ticketNumber: string | null; jobNumber: string | null; jobTicketNumber: string | null }) => {
    if (!hourly) return row.ticketNumber ? `#${row.ticketNumber}` : '—';
    if (row.category === 'shop') return 'Shop';
    if (row.jobNumber) return `Job #${row.jobNumber}${row.jobTicketNumber ? ` · Ticket #${row.jobTicketNumber}` : ''}`;
    return row.ticketNumber ? `#${row.ticketNumber}` : '—';
  };
  const entryRef = (e: any) => payRef({
    category: e.pay_category ?? e.payCategory ?? null,
    ticketNumber: e.ticket_number || null,
    jobNumber: e.job_number ?? e.jobNumber ?? null,
    jobTicketNumber: e.job_ticket_number ?? e.jobTicketNumber ?? null,
  });
  // The API's Overtime row (api#1169). With a rate it is the whole overtime
  // pay; without one the split declined and it is the premium only.
  const otLine = hourly ? (stub?.overtimeLine ?? null) : null;
  // A split row prints the hours it kept, beside the earnings it kept.
  const lineHoursText = (e: any) => {
    const kept = hourly ? stubLineHours(e) : null;
    return kept !== null ? fmtHours(kept) : payEntryHoursText(e);
  };
  const stubAdjRows = hourly ? (stub?.adjustments ?? []) : [];

  const ticketCount = ts.entries.filter(e => !!e.ticket_number).length;

  return (
    <>
      <PageHeaderBand
        eyebrow={`Pay stub · ${formatDateRange(periodStart, periodEnd)}`}
        title={empName}
        onBack={onBack}
        backLabel="Back"
        stats={hourlyBand ? [
          // A US hourly worker is paid by the hour, so the band says hours. Four
          // stats is the band's ceiling: Adjustments stays on the summary card.
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: 'Total pay',
            sub: 'Earnings and adjustments',
            tone: 'accent',
            loading: stubLoading,
            value: money(lines?.total),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Regular hours',
            sub: 'Straight time',
            tone: 'info',
            loading: stubLoading,
            value: fmtHours(stub?.regularHours),
          },
          {
            // Third column: the label ceiling is ~70px and the sub-line's ~80px
            // (circle clearance). "Over 40 a workweek" (116px) swept up to 0.25
            // under Quotes at 1024; this one is at parity across 1024-2560.
            icon: <Timer className="size-4" aria-hidden="true" />,
            label: 'OT hours',
            sub: 'Over 40 hours',
            tone: 'neutral',
            loading: stubLoading,
            value: fmtHours(stub?.overtimeHours),
          },
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'OT pay',
            sub: 'Hours × 1.5 × rate',
            tone: 'success',
            loading: stubLoading,
            // The whole overtime pay only when the API split it; a premium-only
            // figure is not "OT pay", so it reads a dash rather than a number
            // that is a third of the truth.
            value: lines?.overtimeHours != null || lines?.overtime === 0 ? money(lines?.overtime) : '—',
          },
        ] : [
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: 'Total pay',
            sub: 'Earnings and adjustments',
            tone: 'accent',
            loading: stubLoading,
            value: money(lines?.total),
          },
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'Ticket pay',
            sub: 'Before adjustments',
            tone: 'info',
            loading: stubLoading,
            value: money(lines?.ticketEarnings),
          },
          {
            // Third column: the circle's edge crosses it between 1024 and 1048,
            // and the clearance was measured for labels no wider than ~70px.
            icon: <Ticket className="size-4" aria-hidden="true" />,
            label: 'Tickets',
            sub: 'Entries with a ticket',
            tone: 'neutral',
            value: displayValue(ticketCount),
          },
          {
            icon: <HandCoins className="size-4" aria-hidden="true" />,
            label: 'Adjustments',
            sub: 'Bonuses and expenses',
            tone: 'success',
            // ALL-OR-NOTHING, unlike the card's line beneath it. This figure is
            // new with the band and has no existing counterpart, so it takes
            // the rule fe#709 settled: one unreadable amount makes the whole
            // stat a dash, because a sum that skips absent rows understates
            // while looking exactly like a correct figure.
            value: fmtCurrency(sumMoney(userAdjs.map(a => a.amount))),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The stub's own controls. They sit in the BODY and not on the band
              because the band is `overflow: hidden` — an absolutely positioned
              menu opened inside it is clipped at the panel's edge, which reads
              as a dropdown that does not open. */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${
                ts.status === 'approved' ? 'bg-fi-signal-green' : 'bg-fi-signal-blue'
              }`}
            >
              {ts.status === 'approved' ? 'Approved' : 'Active'}
            </span>
            <div className="flex-1" />
            {status === 'open' && (
              <button onClick={() => setShowAddEmpModal(true)}
                className="fi-action fi-action--standard fi-action--primary">
                <Plus size={14} /> Add Manual Entry
              </button>
            )}
            <div className="relative" ref={exportRef}>
              {/* Icon-only, so it needs a name: without one it is unreachable to a
                  screen reader and to getByRole, which is what sent this file's
                  test reaching into the DOM for an <svg>. */}
              <button onClick={() => setShowExportMenu(!showExportMenu)}
                aria-label="Export options"
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-2 text-fieldiq-text hover:text-white">
                <ClipboardList size={16} />
              </button>
              {showExportMenu && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-fieldiq-card border border-fieldiq-border rounded-fi-panel shadow-xl py-1.5 w-48 animate-fade-in">
                  <button onClick={handleExportCSV} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-fieldiq-text hover:bg-fieldiq-dark transition-colors">
                    <FileText size={14} className="text-fieldiq-muted" /> Download CSV
                  </button>
                  <button onClick={handleExportPDF} disabled={!groups || !lines}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-fieldiq-text hover:bg-fieldiq-dark transition-colors disabled:opacity-50">
                    <ClipboardList size={14} className="text-fieldiq-muted" /> Print / PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* The entries register, one row per ticket per pay group (api#1127).
              The amber edge is on this panel and nowhere else; the rows carry
              no rail because an entry has no status. */}
          <section
            data-testid="pay-stub-register"
            aria-labelledby="pay-stub-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Employee timesheet</p>
              <h2 id="pay-stub-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Entries
              </h2>
            </header>
            <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px] text-left text-sm">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Date</th>
                <th scope="col" className="px-4 py-3">Ticket #</th>
                <th scope="col" className="px-4 py-3">Company</th>
                <th scope="col" className="px-4 py-3">Role</th>
                <th scope="col" className="px-4 py-3">Hours</th>
                <th scope="col" className="px-4 py-3">Pay Type</th>
                <th scope="col" className="px-4 py-3 text-right">Earnings</th>
                <th scope="col" className="px-4 py-3">{regionLabel}</th>
                <th scope="col" className="px-4 py-3">Comments</th>
              </tr>
            </thead>
            {stubFailed ? (
              <tbody><tr><td colSpan={9} className="px-4 py-6">
                <LoadFailureNotice error={queryFailureError(stubQuery)} subject="this pay stub" allowed="Admins and office staff" />
              </td></tr></tbody>
            ) : stubLoading ? (
              <tbody><tr><td colSpan={9} className="px-4 py-16 text-center text-sm text-fieldiq-muted">
                <Loader2 size={18} className="inline animate-spin" aria-hidden="true" /> <span className="sr-only">Loading pay stub</span>
              </td></tr></tbody>
            ) : groups === null ? (
              // A 200 with no `groups` key is an API that predates api#1127.
              // Say so rather than drawing an empty register.
              <tbody><tr><td colSpan={9} className="px-4 py-16 text-center text-sm text-fieldiq-muted">
                The server did not return grouped pay lines for this stub.
              </td></tr></tbody>
            ) : groups.length === 0 ? (
              // "No entries yet" is wrong for a worker who is here BECAUSE of
              // an adjustment — a carry-forward correction from a period that
              // has already been paid. Nothing is pending for them; this is
              // the finished state, and "yet" invites the office to go
              // looking for tickets that will never arrive.
              <tbody><tr><td colSpan={9} className="px-4 py-16 text-center text-sm text-fieldiq-muted">
                {userAdjs.length > 0
                  ? 'No ticket work this period — paid by adjustment only.'
                  : 'No entries yet'}
              </td></tr></tbody>
            ) : (
              groups.map((g, i) => {
                const members = groupEntries(g);
                const open = openGroups.has(g.groupKey);
                const bodyId = `pay-group-${i}`;
                const label = g.label ?? '—';
                return (
                  <React.Fragment key={g.groupKey || i}>
                    <tbody>
                      <tr data-testid="pay-group-row" className="border-t border-fieldiq-border hover:bg-fieldiq-dark/50">
                        <td className="px-4 py-3 whitespace-nowrap text-fieldiq-muted">
                          {/* The toggle is a button in the cell, never the row:
                              a group is not a record (CLAUDE.md, fe#716). */}
                          <button
                            type="button"
                            onClick={() => toggleGroup(g.groupKey)}
                            aria-expanded={open}
                            aria-controls={bodyId}
                            aria-label={g.shifts && g.shifts.length > 0
                              ? `${label}, ${g.shifts.length} ${g.shifts.length === 1 ? 'shift' : 'shifts'}`
                              : `${g.ticketNumber ? `Ticket ${g.ticketNumber}, ` : ''}${label}, ${members.length} ${members.length === 1 ? 'line' : 'lines'}`}
                            className="inline-flex items-center gap-1.5 rounded-sm text-fieldiq-muted hover:text-fieldiq-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fi-amber"
                          >
                            {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                            <span aria-hidden="true">{g.date ? formatDateShort(g.date) : '—'}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold text-fieldiq-text">
                          {payRef(g)}
                        </td>
                        <td className="px-4 py-3 text-fieldiq-text">{g.customerName ?? '—'}</td>
                        <td className="px-4 py-3 text-fieldiq-text">{sharedValue(members, e => e.role_on_ticket) ?? '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap tabular-nums text-fieldiq-text">
                          {clockGroupText(g) ?? sharedValue(members, lineHoursText) ?? '—'}
                          {clockGroupText(g) !== null && g.rateReason && (
                            <span data-testid="pay-group-rate-reason" className="block text-[0.7rem] font-normal text-fieldiq-muted">{RATE_REASON_TEXT[g.rateReason]}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold text-fieldiq-text">{label}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap text-fieldiq-text">{money(g.amount)}</td>
                        <td className="px-4 py-3 text-fieldiq-muted">{sharedValue(members, e => regionOf(e.province_state)) ?? '—'}</td>
                        <td className="px-4 py-3 text-fieldiq-muted">{sharedValue(members, e => e.comments) ?? '—'}</td>
                      </tr>
                    </tbody>
                    <tbody id={bodyId}>
                      {/* A clock group opens onto its SHIFTS (api#1193): the
                          wall-clock in and out, and each shift's hours × rate
                          = amount. Ticket groups keep their entry lines. */}
                      {open && g.shifts && g.shifts.length > 0 && g.shifts.map(sh => {
                        const entry = entriesById.get(sh.entryId);
                        return (
                          <tr key={sh.entryId} data-testid="pay-group-shift" className="border-t border-fieldiq-border/60 bg-fieldiq-dark/30 text-xs">
                            <td className="py-2 pl-10 pr-4 whitespace-nowrap text-fieldiq-muted">{sh.date ? formatDateShort(sh.date) : '—'}</td>
                            <td className="px-4 py-2 whitespace-nowrap tabular-nums text-fieldiq-muted">{shiftClockText(sh, timeZone)}</td>
                            <td className="px-4 py-2 text-fieldiq-muted">{entry?.customer_name || '—'}</td>
                            <td className="px-4 py-2 text-fieldiq-text">{entry?.role_on_ticket || '—'}</td>
                            <td className="px-4 py-2 whitespace-nowrap tabular-nums text-fieldiq-text">{hoursTimesRate(sh.hours, sh.rate, sh.amount, money) ?? '—'}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-fieldiq-text">{entry ? fmtPayType(entry.pay_type, entry.percentage_applied) : '—'}</td>
                            <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap text-fieldiq-text">{money(sh.amount)}</td>
                            <td className="px-4 py-2 text-fieldiq-muted">{regionOf(entry?.province_state) ?? '—'}</td>
                            <td className="px-4 py-2 text-fieldiq-muted">{entry?.comments || '—'}</td>
                          </tr>
                        );
                      })}
                      {open && !(g.shifts && g.shifts.length > 0) && members.map(entry => (
                        <tr key={entry.id} data-testid="pay-group-line" className="border-t border-fieldiq-border/60 bg-fieldiq-dark/30 text-xs">
                          <td className="py-2 pl-10 pr-4 whitespace-nowrap text-fieldiq-muted">
                            {entry.entry_date ? formatDateShort(entry.entry_date) : '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-fieldiq-muted">
                            {entryRef(entry)}
                          </td>
                          <td className="px-4 py-2 text-fieldiq-muted">{entry.customer_name || '—'}</td>
                          <td className="px-4 py-2 text-fieldiq-text">{entry.role_on_ticket || '—'}</td>
                          <td className="px-4 py-2 whitespace-nowrap tabular-nums text-fieldiq-text">{lineHoursText(entry)}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-fieldiq-text">
                            {fmtPayType(entry.pay_type, entry.percentage_applied)}
                            {(entry.is_stat_holiday ?? entry.isStatHoliday) === true && (
                              // The API moved this line to Stat holiday by its
                              // description (there is no structured marker);
                              // the badge says which rows that touched.
                              <span className="ml-2 inline-flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink">
                                Stat holiday
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap text-fieldiq-text">{money((hourly ? stubLineEarnings(entry) : entry.earnings) as any)}</td>
                          <td className="px-4 py-2 text-fieldiq-muted">{regionOf(entry.province_state) ?? '—'}</td>
                          <td className="px-4 py-2 text-fieldiq-muted">{entry.comments || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </React.Fragment>
                );
              })
            )}
            {/* A US hourly register adds up to Total pay: its Overtime line and
                its adjustments are rows here, and every figure is the API's. */}
            {!stubFailed && !stubLoading && groups !== null && (otLine || stubAdjRows.length > 0) && (
              <tbody>
                {otLine && (
                  <tr data-testid="pay-overtime-row" className="border-t border-fieldiq-border">
                    <td colSpan={5} className="px-4 py-3"></td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-fieldiq-text">
                      {otLine.rate !== null
                        ? `Overtime ${fmtHours(otLine.hours)} h × ${fmtCurrency(otLine.rate)}`
                        : `Overtime premium, ${fmtHours(otLine.hours)} h`}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap text-fieldiq-text">{money(otLine.amount)}</td>
                    <td colSpan={2}></td>
                  </tr>
                )}
                {stubAdjRows.map(a => (
                  <tr key={a.id} data-testid="pay-adjustment-row" className="border-t border-fieldiq-border">
                    <td colSpan={5} className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-semibold text-fieldiq-text">
                      {getAdjustmentLabel(a.adjustment_type ?? '')}{a.description ? ` — ${a.description}` : ''}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap text-fieldiq-text">{money(a.amount)}</td>
                    <td colSpan={2}></td>
                  </tr>
                ))}
              </tbody>
            )}
            {ts.entries.length > 0 && (
              <tbody>
                <tr className="border-t border-fieldiq-border bg-fieldiq-dark/40">
                  <td colSpan={6} className="px-4 py-3 text-xs font-black uppercase tracking-[0.12em] text-fieldiq-text">Total</td>
                  <td className="px-4 py-3 text-right font-extrabold tabular-nums whitespace-nowrap text-fieldiq-text">{money(hourly ? lines?.total : earnings)}{currency && ` ${currency}`}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            )}
          </table>
            </div>
          </section>

          {/* Named so a test can read the card whole -- its lines and its TOTAL
              are one claim. Every figure is the API's `summary.lines`. */}
          <section data-testid="pay-period-summary" className="overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">What makes it up</p>
              <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Pay Period Summary</h2>
            </header>
          {stubFailed ? (
            <div className="px-4 py-4">
              <LoadFailureNotice error={queryFailureError(stubQuery)} subject="this pay summary" allowed="Admins and office staff" />
            </div>
          ) : (
          <div className="px-4 py-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-text">Ticket earnings:</span>
              <span className="text-white">{money(lines?.ticketEarnings)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-text">Shop time:</span>
              {/* Hours × rate = amount when the API sent the hours (api#1193);
                  the bare amount otherwise, as before. */}
              <span data-testid="pay-summary-shop" className="text-white tabular-nums">
                {hoursTimesRate(lines?.shopHours ?? null, lines?.shopRate ?? null, lines?.shopTime ?? null, money) ?? money(lines?.shopTime)}
              </span>
            </div>
            {/* A US hourly hand's field clock-ins (api#1097). Drawn only when
                such an entry exists, so a Canadian card is unchanged. */}
            {hasHourlyFieldTime && (
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-text">Hourly field time:</span>
                <span className="text-white tabular-nums">
                  {hoursTimesRate(lines?.hourlyFieldHours ?? null, lines?.hourlyFieldRate ?? null, lines?.hourlyFieldTime ?? null, money) ?? money(lines?.hourlyFieldTime)}
                </span>
              </div>
            )}
            {lines?.overtime != null && lines.overtime !== 0 && (
              <div className="flex justify-between text-sm">
                {/* Hours × rate only when the API split the overtime — then the
                    figure is the whole overtime pay, not a premium. */}
                <span className="text-fieldiq-text">
                  {lines.overtimeHours != null && lines.overtimeRate != null
                    ? `Overtime (${fmtHours(lines.overtimeHours)} h × ${fmtCurrency(lines.overtimeRate)}):`
                    : 'Overtime:'}
                </span>
                <span className="text-white">{money(lines.overtime)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-text">Bonuses:</span>
              <span className="text-white">{money(lines?.bonuses)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-fieldiq-text">Stat holiday:</span>
              <div className="flex items-center gap-3">
                {status === 'open' && stub && (
                  <button
                    type="button"
                    onClick={() => setShowStatModal(true)}
                    className="inline-flex items-center gap-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-2 py-0.5 text-xs font-semibold text-fieldiq-text hover:bg-fieldiq-dark"
                  >
                    <Pencil size={12} aria-hidden="true" /> {statAdjs.length > 0 ? 'Edit stat pay' : 'Add stat pay'}
                  </button>
                )}
                <span className="text-white">{money(lines?.statHoliday)}</span>
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-text">Mileage:</span>
              <span className="text-white">{money(lines?.mileage)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-fieldiq-text">Expenses:</span>
              <span className="text-white">{money(lines?.expenses)}</span>
            </div>
            {lines?.other != null && lines.other !== 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-fieldiq-text">Other adjustments:</span>
                <span className="text-white">{money(lines.other)}</span>
              </div>
            )}
            <div className="border-t border-fieldiq-border pt-2 mt-2">
              <div className="flex justify-between text-sm font-bold">
                <span className="text-fieldiq-text">TOTAL:</span>
                <span className="text-fieldiq-text tabular-nums">{money(lines?.total)}</span>
              </div>
              {/* The API reports whether its own lines add up. It should
                  always be 0; a gap is shown, not hidden, so it is reported. */}
              {lines?.residual != null && lines.residual !== 0 && (
                <p className="mt-1 text-xs text-fieldiq-text">
                  The lines above differ from the total by {money(lines.residual)}. Report this — the server's figures disagree.
                </p>
              )}
              <div className="flex justify-between text-xs mt-1">
                <span className="text-fieldiq-muted">Currency:</span>
                <span className="text-fieldiq-text">{currency ?? '—'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-fieldiq-muted">{usTenant ? 'State:' : 'Primary Province:'}</span>
                <span className="text-fieldiq-text">{primaryProvince}</span>
              </div>
            </div>
          </div>
          )}
          </section>
        </div>
      </section>

      {showStatModal && (
        <StatHolidayModal
          periodId={periodId}
          userId={ts.user_id}
          empName={empName}
          statAdjustments={statAdjs}
          onClose={() => setShowStatModal(false)}
          onSaved={refreshStub}
          onPeriodClosed={closed => { setShowStatModal(false); onPeriodClosed(closed); }}
        />
      )}
      {showAddEmpModal && (
        <AddEmployeeModal periodId={periodId} onClose={() => setShowAddEmpModal(false)} onCreated={onRefresh} onPeriodClosed={onPeriodClosed} />
      )}
    </>
  );
}

// ─── Generation flags ───────────────────────────────────────────────────────
//
// `POST /pay-periods/:id/generate` has always returned a `flags[]` array and
// nothing has ever rendered it. That was survivable while every flag was
// advisory. It stops being survivable the moment a flag is the ONLY record
// that a hand was refused pay: a refusal produces no entry, and a missing
// entry is indistinguishable from a line nobody worked.
//
// Severity drives colour, and `error` is deliberately loud -- it means money
// that was expected did not get calculated.
type PayrollFlag = {
  type?: string;
  message?: string;
  ticketNumber?: string | null;
  employeeName?: string | null;
};

const FLAG_STYLES: Record<string, { box: string; text: string; label: string }> = {
  error: {
    box: 'bg-fieldiq-danger/10 border-fieldiq-danger/40',
    text: 'text-fieldiq-danger',
    label: 'Not calculated',
  },
  warning: {
    box: 'bg-amber-500/10 border-amber-500/40',
    text: 'text-amber-400',
    label: 'Check',
  },
  info: {
    box: 'bg-fieldiq-card border-fieldiq-border',
    text: 'text-fieldiq-text',
    label: 'Note',
  },
};

/**
 * A pay entry the server refused, with enough identity to find it.
 *
 * WHY THIS EXISTS RATHER THAN A RING ON A ROW. The other adopters of RowError
 * mark the row the operator typed, because the array they POST is the array
 * they render. This screen does not: Generate posts `draft.entries` — one row
 * per ticket line, straight from the API — and renders `detail.timesheets`, one
 * card per WORKER, reloaded from the server after a save that succeeded. On a
 * refusal nothing is written, so `detail` still describes the last good state
 * and the offending entries are on screen nowhere at all. On the first Generate
 * of an empty period there are no cards whatsoever.
 *
 * `details[].index` is a position in the submitted array, so the entry itself is
 * the only thing that can be marked, and the submitted entry already carries
 * `employeeName`, `ticketNumber` and `entryDate` (payroll.service.ts,
 * GeneratedEntry). Those three name the row in the operator's own vocabulary —
 * a hand, a ticket, a day — which is what they need to go and fix it.
 *
 * The worker's card is ALSO ringed when it happens to be on screen, so the two
 * halves agree; see `refusedUserIds`.
 */
type RefusedEntry = {
  reasons: RowErrorReason[];
  employeeName: string | null;
  ticketNumber: string | null;
  entryDate: string | null;
  userId: string | null;
};

/**
 * Pair the server's `details[]` with the entries that were actually sent.
 *
 * Kept out of the component and exported so it can be tested against a real
 * captured payload without mounting the screen.
 *
 * An index naming no submitted entry is NOT dropped — it is returned with null
 * identity so it still reaches the screen as words. Silently discarding it
 * would turn "the server refused something we cannot place" into "the server
 * said nothing", which is the failure this whole change is about.
 */
export function buildRefusedEntries(
  details: Array<{ index: number; field?: string; message: string }>,
  entries: any[],
): RefusedEntry[] {
  const grouped = groupRowErrors(details);
  return Object.entries(grouped)
    .map(([index, reasons]) => {
      const e = entries?.[Number(index)];
      return {
        reasons,
        employeeName: e?.employeeName ?? null,
        ticketNumber: e?.ticketNumber ?? null,
        entryDate: e?.entryDate ?? null,
        userId: e?.userId ?? null,
      };
    });
}

/**
 * The refused entries, each under the hand and ticket it belongs to.
 *
 * Sits beside GenerationFlags deliberately: a flag says a line produced no pay,
 * this says a line was REFUSED and nothing at all was written. The whole save is
 * refused by the API rather than the bad rows skipped, so "not saved" is the
 * truth for every row in the period, not only these — said once at the top
 * rather than repeated on each line.
 */
function RefusedEntries({ entries, onDismiss }: { entries: RefusedEntry[]; onDismiss: () => void }) {
  if (!entries || entries.length === 0) return null;

  return (
    <div className="mx-4 mt-3 space-y-1.5" data-testid="payroll-refusals">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide">
          Not saved
          <span className="ml-2 text-fieldiq-danger normal-case tracking-normal">
            — {entries.length} {entries.length === 1 ? 'entry was' : 'entries were'} refused, so nothing was written
          </span>
        </span>
        <button onClick={onDismiss} className="text-fieldiq-muted hover:text-white" aria-label="Dismiss refused entries">
          <X size={14} />
        </button>
      </div>

      {entries.map((e, i) => {
        // Never "Unknown" and never a blank: an entry the screen cannot name is
        // still an entry somebody has to find, and saying so is more use than a
        // confident wrong label. CLAUDE.md — an absent value renders as a dash.
        const who = [e.employeeName, e.ticketNumber].filter(Boolean).join(' · ');
        return (
          <div key={i} data-testid="payroll-refusal-row"
            className="border rounded-lg px-3 py-2 bg-fieldiq-danger/10 border-fieldiq-danger/40">
            <p className="text-sm font-medium text-white">
              {who || 'An entry the screen cannot identify —'}
              {e.entryDate && <span className="text-fieldiq-muted font-normal"> · {formatDateShort(e.entryDate)}</span>}
            </p>
            <RowError reasons={e.reasons} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The approve gate's refusal (api#826, api#827): approved shop shifts in this
 * period that have no pay entry. The API answers 409 with a message naming them
 * and `details.unfiledShopShifts`, one row per shift. The message is shown as
 * written in the red band; these rows are listed under it so the office can see
 * which shift is which without parsing a sentence.
 *
 * `workDate` and `hours` are rendered exactly as sent — the API's own message
 * quotes the same two values, and a screen that reformatted them would be a
 * second spelling of one answer. Only `reason` is put into words, from an
 * exhaustive map; a reason the screen does not know reads as a dash, never as
 * one of the two it does.
 */
export type UnfiledShopShift = {
  timesheetId: string | null;
  workerName: string | null;
  workDate: string | null;
  hours: number | string | null;
  reason: string | null;
};

export const UNFILED_SHIFT_REASON_LABELS: Record<'missing_entry' | 'unfiled_rate_gap', string> = {
  // The rate resolves; Generate simply has not filed the shift yet.
  missing_entry: 'Not filed — check the shop rate, then run Generate',
  // The worker has shop rates, but none whose dates cover this shift.
  unfiled_rate_gap: 'No shop rate covers this date — fix the rate\'s effective date, then run Generate',
};

/** Null unless the error carries the approve gate's shift list. */
export function readUnfiledShopShifts(err: any): UnfiledShopShift[] | null {
  const rows = err?.response?.data?.details?.unfiledShopShifts;
  if (!Array.isArray(rows)) return null;
  return rows.map((r: any) => ({
    timesheetId: r?.timesheetId ?? null,
    workerName: r?.workerName ?? null,
    workDate: r?.workDate ?? null,
    hours: r?.hours ?? null,
    reason: r?.reason ?? null,
  }));
}

function UnfiledShopShifts({ shifts }: { shifts: UnfiledShopShift[] }) {
  if (shifts.length === 0) return null;
  return (
    <div data-testid="payroll-unfiled-shop-shifts"
      className="rounded-fi-panel border border-fieldiq-danger/30 bg-fieldiq-card">
      <p className="border-b border-fieldiq-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-fieldiq-muted">
        Shop shifts blocking approval
      </p>
      <ul className="divide-y divide-fieldiq-border">
        {shifts.map((s, i) => {
          const label = s.reason && s.reason in UNFILED_SHIFT_REASON_LABELS
            ? UNFILED_SHIFT_REASON_LABELS[s.reason as keyof typeof UNFILED_SHIFT_REASON_LABELS]
            : '—';
          return (
            <li key={s.timesheetId ?? i} data-testid="payroll-unfiled-shop-shift"
              className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-sm">
              <span className="font-medium text-fieldiq-text">{s.workerName ?? '—'}</span>
              <span className="text-fieldiq-muted">{s.workDate ?? '—'}</span>
              <span className="text-fieldiq-muted">{s.hours === null || s.hours === '' ? '—' : `${s.hours} h`}</span>
              <span className="text-fieldiq-text">{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The three export formats, in one place.
 *
 * It was two byte-identical copies in the same component — one for an open
 * period, one for an approved one — differing only in which way the menu
 * opens. Two copies of a menu whose items fire irreversible-ish writes
 * (`export` stamps `exported_at`, after which reopen is refused for good) is
 * the shape that lets one grow a format the other does not have.
 *
 * `above` is the only difference that was ever real: the approved row sits at
 * the bottom of the page, so its menu opens upward.
 */
function ExportMenu({ above, onPick, onClose }: {
  above?: boolean;
  onPick: (format?: string) => void;
  onClose: () => void;
}) {
  const item = 'w-full flex items-center gap-2 px-4 py-2.5 text-sm text-fieldiq-text hover:bg-fieldiq-dark transition-colors';
  return (
    <div className={`absolute right-0 z-50 w-52 animate-fade-in rounded-fi-panel border border-fieldiq-border bg-fieldiq-card py-1.5 shadow-xl ${above ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
      <button onClick={() => { onClose(); onPick(); }} className={item}>
        <FileText size={14} className="text-fieldiq-muted" /> Export CSV
      </button>
      <button onClick={() => { onClose(); onPick('quickbooks'); }} className={item}>
        <DollarSign size={14} className="text-fi-signal-blue" /> Export CSV (QuickBooks)
      </button>
      <button onClick={() => { onClose(); onPick('sage'); }} className={item}>
        <DollarSign size={14} className="text-fi-signal-green" /> Export CSV (Sage)
      </button>
      <button onClick={() => { onClose(); onPick('dynamics'); }} className={item}>
        <DollarSign size={14} className="text-fi-signal-slate" /> Export to Dynamics 365
      </button>
    </div>
  );
}

function GenerationFlags({ flags, onDismiss }: { flags: PayrollFlag[]; onDismiss: () => void }) {
  if (!flags || flags.length === 0) return null;

  // Errors first — they are the ones that cost somebody money.
  const order = (f: PayrollFlag) => (f.type === 'error' ? 0 : f.type === 'warning' ? 1 : 2);
  const sorted = [...flags].sort((a, b) => order(a) - order(b));
  const errorCount = flags.filter(f => f.type === 'error').length;

  return (
    <div className="mx-4 mt-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-fieldiq-muted uppercase tracking-wide">
          Generation report
          {errorCount > 0 && (
            <span className="ml-2 text-fieldiq-danger normal-case tracking-normal">
              — {errorCount} line{errorCount !== 1 ? 's' : ''} produced no pay
            </span>
          )}
        </span>
        <button onClick={onDismiss} className="text-fieldiq-muted hover:text-white" aria-label="Dismiss generation report">
          <X size={14} />
        </button>
      </div>

      {sorted.map((f, i) => {
        const style = FLAG_STYLES[f.type || 'info'] ?? FLAG_STYLES.info;
        return (
          <div key={i} className={`border rounded-lg px-3 py-2 ${style.box}`}>
            <div className="flex items-start gap-2">
              <span className={`text-[10px] font-bold uppercase shrink-0 mt-0.5 ${style.text}`}>
                {style.label}
              </span>
              <div className="min-w-0">
                <p className={`text-sm ${f.type === 'error' ? style.text : 'text-white'}`}>{f.message}</p>
                {(f.ticketNumber || f.employeeName) && (
                  <p className="text-xs text-fieldiq-muted mt-0.5">
                    {[f.employeeName, f.ticketNumber].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Pay Period Detail View ─────────────────────────────────────────────────

/** generatePayroll's opening words when a period has no ticket pay. */
const NO_TICKETS_FLAG = /^No tickets with approved pay assignments/;

function PayPeriodDetailView({ periodId, onBack }: { periodId: string; onBack: () => void }) {
  const timeZone = useTenantTimeZone();
  const currency = useTenantCurrency();
  const [detail, setDetail] = useState<PayPeriodDetail | null>(null);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [showAdjModal, setShowAdjModal] = useState(false);
  const [showAddEmpModal, setShowAddEmpModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Timesheet | null>(null);
  const [error, setError] = useState('');
  /**
   * The period's READ failing, kept apart from `error`.
   *
   * `error` is an ACTION's refusal — a save, an approve, a mark-paid — and
   * belongs in the band above the list, where the Reopen pointer can sit
   * beside it. This one means the period never arrived, so there is no list
   * to put a band above; it takes the whole panel. Folding the two together
   * is what let a 403 on the read render as "Pay period not found".
   */
  const [loadError, setLoadError] = useState<unknown>(null);
  // Flags from the last Generate. The API has always returned these on
  // `POST /pay-periods/:id/generate` and this screen dropped them on the floor
  // -- `handleGenerate` read `entries` and `employeeSummaries` and never
  // touched `flags`. They are held in their own state rather than derived from
  // `detail`, because `loadDetail()` refetches the SAVED period and the flags
  // describe the GENERATION that produced it. Nothing persists them server-side.
  const [genFlags, setGenFlags] = useState<PayrollFlag[]>([]);
  /**
   * `draft.shopEntries` from the same Generate, kept for the same reason as
   * the flags: the save reloads the period and the draft is gone. Rendered
   * only when a shift was priced at an hourly rate (US hourly pay, api#1097) —
   * see `hasHourlyClockPay`, which keeps every Canadian screen as it was.
   */
  const [genClockPay, setGenClockPay] = useState<ClockPayEntry[]>([]);
  /**
   * Entries the last Save was REFUSED for, paired back to what was sent.
   *
   * Held separately from `genFlags` because they answer different questions and
   * both can be true at once: a flag says a ticket line produced no pay entry, a
   * refusal says the entries that WERE produced could not be written. Folding
   * them together would let a refusal be dismissed by clearing a flag.
   */
  const [refusedEntries, setRefusedEntries] = useState<RefusedEntry[]>([]);
  /** The approve gate's shift list; rides with the red band's message. */
  const [unfiledShifts, setUnfiledShifts] = useState<UnfiledShopShift[]>([]);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showPaidConfirm, setShowPaidConfirm] = useState(false);
  /**
   * The period the open Reopen confirmation will reopen; null while it is shut.
   * The header's control always names this period. The error band's names the
   * period its refusal says refused, which is not guaranteed to be this one.
   */
  const [reopenTarget, setReopenTarget] = useState<string | null>(null);
  /**
   * The error band is showing a closed-period refusal whose `canReopen` was
   * true: the period its Reopen button targets, else null. Cleared with the
   * error, never left pointing at a stale message.
   */
  const [errorReopenTarget, setErrorReopenTarget] = useState<string | null>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Mirrors requireRole('admin', { strict: true }) on the endpoint. Hiding the
  // control is not the enforcement -- the API is -- it just keeps someone from
  // firing a ONE-WAY action they would be refused for anyway. A button that
  // can only answer 403 gets reported as broken.
  // ONE FLAG PER API GATE — see usePayrollPermissions. Approve, mark-paid,
  // reopen and delete are STRICT admin; generate, save, export, add-employee
  // and adjustments are admin-or-office-staff and DO admit an elevated
  // dispatcher. Reading any of those off another is how this screen came to
  // offer `Approve All` to every role that could open a period.
  const perms = usePayrollPermissions();
  const { canMarkPaid, canReopen, canApprove, canEditPeriod } = perms;
  // US tenants are paid from clock-ins (api#1097), so the Generate wording names
  // them. False while the locale loads (it defaults to CA) and with no provider,
  // so a Canadian screen can never read the US wording.
  const usTenant = useContext(LocaleContext)?.isAmerican === true;

  useEffect(() => {
    if (!showExportDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showExportDropdown]);

  const loadDetail = useCallback(async () => {
    try {
      const [detailRes, adjRes] = await Promise.all([
        api.payroll.getPayPeriod(periodId),
        api.payroll.listAdjustments(periodId),
      ]);
      setDetail(detailRes.data.data);
      setAdjustments(adjRes.data.data);
    } catch (err) {
      // THE RAW ERROR, not a sentence. `GET /payroll/pay-periods/:id` and its
      // adjustments sibling are both `requireRole('admin','office_staff')`, so
      // a supervisor, a logistics user or a plain dispatcher — all of whom the
      // UNGATED list happily showed this period to — get a 403 here. The old
      // string turned that into "Failed to load pay period", and the branch
      // below then rendered "Pay period not found", which is a claim about the
      // DATA made from a fact about PERMISSION. classifyLoadFailure needs the
      // status, so the object is what gets kept.
      setLoadError(err);
    } finally { setLoading(false); }
  }, [periodId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const handleGenerate = async () => {
    setActionLoading('generate'); setError(''); setGenFlags([]); setGenClockPay([]); setRefusedEntries([]); setUnfiledShifts([]);
    // The entries this run actually POSTed, so a refusal's `index` can be paired
    // with the row it names. Scoped to the attempt rather than held in state:
    // the refusal describes THIS request, and by the time it lands a second
    // Generate may already have replaced the draft.
    let submitted: any[] = [];
    try {
      const res = await api.payroll.generatePayroll(periodId);
      const draft = res.data.data;
      const entries = draft?.entries ?? draft;

      // Captured BEFORE the save, and deliberately not cleared by it. An error
      // flag means a line produced NO pay entry -- the entry is simply absent
      // from `entries`, which looks identical to a line nobody worked. The
      // flag is the only thing that says a hand went unpaid and why.
      const shopEntries = Array.isArray(draft?.shopEntries) ? draft.shopEntries : [];
      const hourly = hasHourlyClockPay(shopEntries);
      const flags: PayrollFlag[] = Array.isArray(draft?.flags) ? draft.flags : [];
      // The API's "no tickets with approved pay assignments" note is true and
      // beside the point on a US hourly period: the pay came from clock-ins.
      // The flag carries no key, so it is matched on the API's own opening
      // words (payroll.service.ts generatePayroll) — and only when clock pay
      // was priced, so a Canadian period keeps it exactly as before.
      setGenFlags(hourly ? flags.filter((f) => !NO_TICKETS_FLAG.test(f?.message ?? '')) : flags);
      setGenClockPay(shopEntries);

      // Save when there is pay to write OR a carry-forward adjustment to land.
      //
      // This read `entries.length > 0`, and that alone made the carry-forward
      // remedy unreachable from the product. A correction to an already-paid
      // period is supposed to land as an adjustment in the CURRENT period —
      // three API error messages say so, and it is the only route offered once
      // reopen refuses — but the current period frequently has no ticket pay in
      // it yet, so Generate returned zero entries, this line skipped Save, and
      // the money never reached the header.
      //
      // The API's matching halves are generatePayroll (which now summarises a
      // worker who has an adjustment and no tickets), savePayrollEntries (which
      // now creates their timesheet), and the route (which no longer 400s on an
      // empty array carrying an adjustment). All four had to move; any one of
      // them left alone puts the money back in the hole.
      const summaries = draft?.employeeSummaries ?? [];
      const carriesAdjustment = summaries.some((s: any) => Number(s?.adjustmentsTotal) !== 0);

      // The third way a Generate moves money: US hourly clock-in pay. It is NOT
      // in `entries` — hourly workers' ticket lines are dropped — and it does not
      // travel in the save body either: savePayrollEntries re-prices clock-ins
      // from the database and writes them, and the save route accepts an empty
      // `entries` when there is clock pay to write. Without this, a period whose
      // only pay is clock-ins showed the report and saved nothing (0 workers,
      // Approve All disabled). Gated on an hourly-priced shift with an amount so
      // a Canadian period's Generate takes exactly the path it took before.
      const carriesClockPay = hourly && shopEntries.some((e: any) => e?.amount !== null && e?.amount !== undefined);

      if ((entries && entries.length > 0) || carriesAdjustment || carriesClockPay) {
        submitted = entries ?? [];
        await api.payroll.savePayroll(periodId, {
          entries: submitted,
          employeeSummaries: draft?.employeeSummaries,
        });
      }
      await loadDetail();
    } catch (err: any) {
      const data = err?.response?.data;

      /*
       * A refusal about N entries says WHICH entry.
       *
       * Gated on `details` being an array, NOT on the status code. The save
       * route answers 400 for a malformed row and 404 when a row points at
       * another company's worker or ticket — both carry `details`, and both name
       * rows the office has to go and fix. Keying this on 400, the way the
       * ticket editors reasonably do, would read the 404's rows as absent and
       * leave the operator with a banner saying a worker "is not in your
       * company" and no idea which of forty that is. Measured against a running
       * API 2026-09-03: shape errors 400, foreign pointers 404, both with
       * details[].
       */
      if (Array.isArray(data?.details)) {
        setRefusedEntries(buildRefusedEntries(data.details, submitted));
      }
      const closed = readPeriodClosed(err);
      if (closed) { await showPeriodClosed(closed); return; }
      setError(data?.error || 'Failed to generate payroll');
    } finally { setActionLoading(''); }
  };

  /**
   * A write was refused because the period is no longer open — another tab or
   * another admin approved it under this one. The message is the API's, as
   * written. The refetch is what makes the header tell the truth again, so the
   * Reopen control the message names is actually on screen.
   */
  const showPeriodClosed = async (closed: PeriodClosed) => {
    await loadDetail();
    setError(closed.message);
    setErrorReopenTarget(closed.canReopen ? reopenTargetFor(closed, periodId) : null);
  };

  const clearError = () => { setError(''); setErrorReopenTarget(null); setUnfiledShifts([]); };

  const handleReopen = async () => {
    if (!reopenTarget) return;
    const target = reopenTarget;
    setActionLoading('reopen'); clearError();
    try {
      const result = await reopenPayPeriod(target);
      setReopenTarget(null);
      await loadDetail();
      // Success toasts; a refusal is shown as written, in the red band.
      if (result.ok) toast.success(result.message); else setError(result.message);
    } finally { setActionLoading(''); }
  };

  const handleApprove = async () => {
    setActionLoading('approve'); setError(''); setUnfiledShifts([]);
    try { await api.payroll.approvePayPeriod(periodId); await loadDetail(); }
    catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve');
      setUnfiledShifts(readUnfiledShopShifts(err) ?? []);
    } finally { setActionLoading(''); }
  };

  const handleExport = async (format?: string) => {
    setActionLoading('export'); setError('');
    try {
      const res = await api.payroll.exportPayroll(periodId, format);
      // Every format is a CSV body (api#797 retired the .iif name the QuickBooks
      // body never matched). The name is the API's own, from Content-Disposition.
      // The live API's CORS config exposes Set-Cookie AND Content-Disposition
      // (preflight-verified in fe#767), so a cross-origin build reads the name.
      // The fallback covers a response with no such header; it is the name the
      // API sends, so both paths save the same file. Update this if the API
      // ever names formats differently.
      const fromApi = filenameFromContentDisposition(res.headers?.['content-disposition']);
      const name = fromApi ?? `payroll-${periodId}.csv`;
      const filename = /\.csv$/i.test(name) ? name : `${name}.csv`;
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      await loadDetail();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to export'); } finally { setActionLoading(''); }
  };

  const handleMarkPaid = async () => {
    setActionLoading('mark-paid'); setError('');
    try {
      const res = await api.payPeriods.markPaid(periodId);
      setShowPaidConfirm(false);
      // The API distinguishes "marked paid" from "was already paid" in its
      // own message; pass it through rather than asserting the first.
      toast.success(res.data?.message || 'Pay period marked paid');
      await loadDetail();
    } catch (err: any) {
      // Deliberately NOT toast.success. FE #421 routed a refusal into this
      // screen family's success toast and "You don't have permission" rendered
      // under a green checkmark for a month. A refusal goes to the red band.
      setShowPaidConfirm(false);
      setError(err.response?.data?.error || 'Failed to mark the pay period paid');
    } finally { setActionLoading(''); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-fieldiq-muted" /></div>;
  if (!detail) {
    /*
     * THREE STATES, NOT TWO. A period that did not arrive is either refused or
     * unreachable, and the two have different remedies: "ask an admin" is
     * useless to someone in a dead zone, and "try again" is useless to a
     * supervisor the API will refuse every time. `loadError` carries the
     * status; LoadFailureNotice does the split. A genuine 404 from another
     * tenant's id still reads as unreachable, which is the honest answer — the
     * API deliberately does not distinguish "not yours" from "not there".
     */
    return (
      <>
        <PageHeaderBand eyebrow="Payroll cycle" title="Pay period" onBack={onBack} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
              <LoadFailureNotice
                error={loadError ?? new Error('Pay period not found')}
                subject="this pay period"
                allowed="Admins and office staff"
              />
            </section>
          </div>
        </section>
      </>
    );
  }

  // If viewing a specific employee
  if (selectedEmployee) {
    return (
      <EmployeeDetailView
        periodId={periodId}
        ts={selectedEmployee}
        adjustments={adjustments}
        status={detail.status}
        periodStart={detail.period_start}
        periodEnd={detail.period_end}
        onBack={() => { setSelectedEmployee(null); loadDetail(); }}
        onRefresh={loadDetail}
        onPeriodClosed={closed => { setSelectedEmployee(null); showPeriodClosed(closed); }}
      />
    );
  }

  /**
   * The period's money, ALL-OR-NOTHING.
   *
   * This was `Number(ts.total_earnings || 0)` summed over the workers. That
   * `|| 0` is the silent-blank-field family pointed at the one figure an admin
   * reads before pressing a one-way button: a worker the server could not
   * score contributed nothing and the total came out confident and short by
   * one person's pay, with nothing on screen to say a row had been dropped.
   * `sumMoney` returns null the moment any contributor is absent, so the
   * figure is a dash instead of an understatement. A real 0 still contributes
   * 0, and an empty period still totals $0.00 — nothing to pay is not unknown.
   *
   * ONE figure, three readers: the band stat, the register footer and the
   * mark-as-paid confirmation. They must not be able to disagree.
   */
  const companyRegion = detail.companyRegion ?? detail.company_region ?? null;
  const totalPay = sumMoney([
    ...detail.timesheets.map(ts => ts.total_earnings),
    // Every timesheet's overtime premium, which total_earnings does not hold.
    // Canadian timesheets carry 0 here (the jurisdiction engine is US-only),
    // so their figure is unchanged.
    ...detail.timesheets.map(ts => ts.overtime_earnings),
    ...adjustments.map(a => a.amount),
  ]);
  const adjustmentsTotal = sumMoney(adjustments.map(a => a.amount));
  const entryCount = detail.timesheets.reduce((s, ts) => s + (ts.entries?.length ?? 0), 0);
  const status = detail.status;
  /**
   * The workers a refused entry belongs to, for the ring on their card.
   *
   * The card is a per-WORKER roll-up and the refusal is per ENTRY, so this is a
   * set, not a lookup: two refused entries for one hand ring one card once. A
   * refusal whose userId could not be read rings nothing — the block above still
   * names it, and ringing an arbitrary card would be worse than ringing none.
   */
  const refusedUserIds = new Set(
    refusedEntries.map(e => e.userId).filter((id): id is string => !!id)
  );

  return (
    <>
      <PageHeaderBand
        eyebrow="Payroll cycle"
        title={formatDateRange(detail.period_start, detail.period_end)}
        onBack={onBack}
        backLabel="Back"
        actions={
          <>
            <StatusBadge status={status} />
            {/*
              Reopen lives on the BAND, beside the badge it changes, and
              nowhere in the action row at the bottom. That row holds Export,
              whose menu items fire with no confirmation and stamp exported_at —
              after which reopen is refused for good — and Mark as paid, which
              is one-way. The whole employee list sits between this and both,
              and PayrollScreen.reopen.test pins that ordering.

              A secondary action on the band takes a white/25 border and NO
              colour class: it inherits the band's near-white ink, where a
              plain white-text utility would be remapped to the heading colour
              in light mode.
            */}
            {status === 'approved' && canReopen && (
              <button onClick={() => setReopenTarget(periodId)} disabled={!!actionLoading}
                className="fi-action fi-action--standard border border-white/25 disabled:opacity-50">
                {actionLoading === 'reopen' ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Reopen
              </button>
            )}
          </>
        }
        stats={[
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'Workers',
            sub: 'On this period',
            tone: 'accent',
            value: displayValue(detail.timesheets.length),
          },
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: 'Gross pay',
            sub: 'Earnings and adjustments',
            tone: 'info',
            value: money(totalPay),
          },
          {
            // Third column: the hero circle's edge crosses it between 1024 and
            // 1048, and the clearance was measured for labels no wider than
            // about 70px. 'Entries' is well inside that; see the PR's sweep.
            icon: <Receipt className="size-4" aria-hidden="true" />,
            label: 'Entries',
            sub: usTenant ? 'Pay lines' : 'Priced ticket lines',
            tone: 'neutral',
            value: displayValue(entryCount),
          },
          {
            icon: <HandCoins className="size-4" aria-hidden="true" />,
            label: 'Adjustments',
            sub: 'Bonuses and expenses',
            tone: 'success',
            value: money(adjustmentsTotal),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">

      {error && (
        <div className="text-sm text-fieldiq-danger bg-fieldiq-danger/10 border border-fieldiq-danger/30 rounded-fi-panel p-3 flex items-center justify-between gap-3">
          <span>{error}</span>
          <div className="flex items-center gap-2 shrink-0">
            {/* Opens the SAME confirmation as the band control — one way in. */}
            {errorReopenTarget && canReopen && (
              <ReopenPeriodButton onClick={() => setReopenTarget(errorReopenTarget)} disabled={!!actionLoading} />
            )}
            <button onClick={clearError} aria-label="Dismiss"><X size={16} /></button>
          </div>
        </div>
      )}

      {error && <UnfiledShopShifts shifts={unfiledShifts} />}

      <RefusedEntries entries={refusedEntries} onDismiss={() => setRefusedEntries([])} />

      <GenerationFlags flags={genFlags} onDismiss={() => setGenFlags([])} />
      <ClockPayEntries entries={genClockPay} onDismiss={() => setGenClockPay([])} />

      {/* The employee register. The amber edge is on this panel and nowhere
          else; rows carry no status rail because a timesheet's two states
          (draft / approved) are the period's business, not a per-row status
          the rail vocabulary covers. */}
      <section
        data-testid="payroll-employee-register"
        aria-labelledby="payroll-employee-register-title"
        className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
      >
        <header className="border-b border-fieldiq-border px-4 py-4">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Who is on this period</p>
          <h2 id="payroll-employee-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
            Employees
          </h2>
        </header>

        {detail.timesheets.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <Users size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p className="text-fieldiq-text font-semibold">No employees on this period yet</p>
            <p className="text-sm text-fieldiq-muted mt-1">
              {canEditPeriod
                ? (usTenant
                  ? 'Use "Generate Pay" to price approved clock-ins and ticket work, or "Add Employee" for a manual entry.'
                  : 'Use "Generate from Tickets" to price approved ticket work, or "Add Employee" for a manual entry.')
                : (usTenant
                  ? 'Pay is built by an admin or office staff from approved clock-ins and ticket work.'
                  : 'Pay is built by an admin or office staff from approved ticket work.')}
            </p>
          </div>
        ) : (
          /* A min width rather than a card stack below md. The old stack was a
             SECOND copy of every row — it duplicated the row target and the
             refusal ring, and a scoped query could match either copy. The rows
             scroll sideways, which is what the standard asks of a wide table. */
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr>
                  <th scope="col" className="px-4 py-3">Employee</th>
                  <th scope="col" className="px-4 py-3 text-right">Earned</th>
                  <th scope="col" className="px-4 py-3">{usTenant ? 'State' : 'Province'}</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="w-10 px-4 py-3"><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                  {detail.timesheets.map(ts => {
                    const userAdjs = adjustments.filter(a => a.user_id === ts.user_id);
                    // Same all-or-nothing rule as the period total above: a
                    // worker whose earnings the server could not score reads a
                    // dash, never a figure short by their adjustments.
                    const total = sumMoney([ts.total_earnings, ts.overtime_earnings, ...userAdjs.map(a => a.amount)]);
                    const isApproved = ts.status === 'approved';
                    // A shop-only worker's entries carry no region; on a US
                    // tenant they worked in the company's own state.
                    const province = ts.entries?.[0]?.province_state
                      || (usTenant ? companyRegion : null) || '—';
                    const open = () => setSelectedEmployee(ts);
                    return (
                      <tr key={ts.id}
                        data-testid="payroll-employee-row"
                        tabIndex={0}
                        className={`cursor-pointer border-t border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${rowErrorRingClass(refusedUserIds.has(ts.user_id))}`}
                        onClick={open}
                        onKeyDown={e => {
                          /*
                           * NO role="button" ON THE <tr>, and that is not the
                           * fe#647 reason — this row holds no inner controls.
                           * An explicit role REPLACES the implicit `row` one,
                           * which takes the row out of the table's structure:
                           * the cells lose their column association for a
                           * screen reader, and `getAllByRole('row')` stops
                           * finding it. Both halves showed up the moment it
                           * was tried; the second is what PayrollScreen's own
                           * suites query by. The row is reachable and operable
                           * without it.
                           */
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                        }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-fi-amber/20 text-xs font-black text-fi-signal-amber">
                              {ts.initials || getInitials(ts.first_name, ts.last_name)}
                            </div>
                            <span className="font-semibold text-fieldiq-text">{ts.first_name} {ts.last_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-extrabold tabular-nums whitespace-nowrap text-fieldiq-text">{money(total)}</td>
                        <td className="px-4 py-3 text-fieldiq-muted">{province}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${
                            isApproved ? 'bg-fi-signal-green' : 'bg-fi-signal-blue'
                          }`}>
                            {isApproved ? 'Approved' : 'Active'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <ChevronRight size={16} className="text-fieldiq-muted" />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* Approved/Exported metadata — the panel's footer line. */}
        {(detail.approved_at || detail.exported_at) && (
          <footer className="flex flex-wrap gap-x-6 gap-y-1 border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
            {detail.approved_at && <span>Approved: {formatDateFull(detail.approved_at, timeZone)}</span>}
            {detail.exported_at && <span>Exported to QB: {formatDateFull(detail.exported_at, timeZone)}</span>}
          </footer>
        )}
      </section>

      {/*
        THE ACTION ROW, AND EVERY CONTROL ON ITS OWN API GATE (fe#691).
        This row used to be gated only twice — `canMarkPaid` on both Mark as
        paid buttons — and everything else was shown to whoever could open the
        period. `Approve All` is the one that cost something: approve is
        `requireRole('admin', { strict: true })`, so office staff AND an
        elevated dispatcher both saw a green button that could only ever answer
        403, on the action that closes a pay period. A button that can only
        answer 403 gets reported as broken.

        Export, Generate, Add Employee and Adjustment are the NON-strict
        admin/office_staff doors, so an elevated dispatcher keeps them. View is
        local state and reaches no endpoint, so it is gated on nothing.
      */}
      <div>
        <div className="flex flex-wrap gap-2">
          {status === 'open' && (
            <>
              <button onClick={() => setSelectedEmployee(detail.timesheets[0] || null)}
                disabled={detail.timesheets.length === 0}
                className="fi-action fi-action--standard fi-action--secondary flex-1 justify-center disabled:opacity-40">
                <Eye size={16} /> View All
              </button>
              {canApprove && (
                <button onClick={handleApprove} disabled={!!actionLoading || detail.timesheets.length === 0}
                  className="fi-action fi-action--standard fi-action--primary flex-1 justify-center disabled:opacity-50">
                  {actionLoading === 'approve' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Approve All
                </button>
              )}
              {canEditPeriod && (
                <div className="relative" ref={exportDropdownRef}>
                  <button onClick={() => setShowExportDropdown(!showExportDropdown)} disabled={!!actionLoading}
                    aria-label="Export options"
                    className="fi-action fi-action--standard fi-action--secondary disabled:opacity-50">
                    {actionLoading === 'export' ? <Loader2 size={16} className="animate-spin" /> : <ClipboardList size={16} />}
                  </button>
                  {showExportDropdown && <ExportMenu onPick={handleExport} onClose={() => setShowExportDropdown(false)} />}
                </div>
              )}
            </>
          )}
          {status === 'approved' && (
            <>
              <button onClick={() => detail.timesheets[0] && setSelectedEmployee(detail.timesheets[0])}
                className="fi-action fi-action--standard fi-action--secondary flex-1 justify-center">
                <Eye size={16} /> View
              </button>
              {canEditPeriod && (
                <div className="relative flex-1" ref={exportDropdownRef}>
                  <button onClick={() => setShowExportDropdown(!showExportDropdown)} disabled={!!actionLoading}
                    className="fi-action fi-action--standard fi-action--secondary w-full justify-center disabled:opacity-50">
                    {actionLoading === 'export' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    Export CSV
                  </button>
                  {showExportDropdown && <ExportMenu above onPick={handleExport} onClose={() => setShowExportDropdown(false)} />}
                </div>
              )}
              {canMarkPaid && (
                <button onClick={() => setShowPaidConfirm(true)} disabled={!!actionLoading}
                  className="fi-action fi-action--standard fi-action--primary flex-1 justify-center disabled:opacity-50">
                  {actionLoading === 'mark-paid' ? <Loader2 size={16} className="animate-spin" /> : <DollarSign size={16} />}
                  Mark as paid
                </button>
              )}
            </>
          )}
          {status === 'exported' && (
            <>
              <button onClick={() => detail.timesheets[0] && setSelectedEmployee(detail.timesheets[0])}
                className="fi-action fi-action--standard fi-action--secondary flex-1 justify-center">
                <Eye size={16} /> View
              </button>
              {canMarkPaid && (
                <button onClick={() => setShowPaidConfirm(true)} disabled={!!actionLoading}
                  className="fi-action fi-action--standard fi-action--primary flex-1 justify-center disabled:opacity-50">
                  {actionLoading === 'mark-paid' ? <Loader2 size={16} className="animate-spin" /> : <DollarSign size={16} />}
                  Mark as paid
                </button>
              )}
            </>
          )}
          {/* Paid is terminal. There is no action left, so this branch offers
              none -- an absent control rather than one that answers 409. */}
          {status === 'paid' && (
            <button onClick={() => detail.timesheets[0] && setSelectedEmployee(detail.timesheets[0])}
              className="fi-action fi-action--standard fi-action--secondary">
              <Eye size={16} /> View Only
            </button>
          )}
        </div>

        {/* Extra actions for open periods */}
        {status === 'open' && canEditPeriod && (
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={handleGenerate} disabled={!!actionLoading}
              className="fi-action fi-action--standard flex-1 justify-center bg-fi-signal-blue text-fi-signal-ink disabled:opacity-50">
              {actionLoading === 'generate' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {usTenant ? 'Generate Pay' : 'Generate from Tickets'}
            </button>
            <button onClick={() => setShowAddEmpModal(true)}
              className="fi-action fi-action--standard fi-action--primary flex-1 justify-center">
              <UserPlus size={14} /> Add Employee
            </button>
            <button onClick={() => setShowAdjModal(true)}
              className="fi-action fi-action--standard fi-action--secondary">
              <Plus size={14} /> Adjustment
            </button>
          </div>
        )}

        {/* The Locations pattern (#686): the period stays readable, and one
            line says who may act on it. Not a refusal notice — this reader can
            see every figure here; they simply cannot be the one to move it. */}
        {!canEditPeriod && !canApprove && !canMarkPaid && (
          <p data-testid="payroll-readonly-note" className="mt-3 text-xs text-fieldiq-muted">
            Only an admin or office staff can build or export a pay period, and only an admin can
            approve it or mark it paid.
          </p>
        )}
      </div>

        </div>
      </section>

      {showAdjModal && <AdjustmentModal periodId={periodId} timesheets={detail.timesheets} onClose={() => setShowAdjModal(false)} onCreated={loadDetail} onPeriodClosed={showPeriodClosed} />}
      {showAddEmpModal && <AddEmployeeModal periodId={periodId} onClose={() => setShowAddEmpModal(false)} onCreated={loadDetail} onPeriodClosed={showPeriodClosed} />}

      <ReopenPayPeriodConfirm
        isOpen={reopenTarget !== null}
        periodLabel={reopenTarget === periodId
          ? formatDateRange(detail.period_start, detail.period_end)
          : 'The pay period that refused this change'}
        loading={actionLoading === 'reopen'}
        onConfirm={handleReopen}
        onCancel={() => setReopenTarget(null)}
      />

      {/*
        The sentence below is read once, by someone about to do something they
        cannot take back, so every clause in it has to be literally true of the
        API as it stands:

          - "can't be reopened"  POST /pay-periods/:id/reopen admits only
                                 status = 'approved' (pay-periods.service.ts)
          - "can't be deleted"   DELETE /pay-periods/:id requires 'open'
                                 (pay-periods.routes.ts)
          - "won't go back to
             Approved or Exported"  both export writers carry a status
                                 allowlist as of API #447; before that PR this
                                 clause was FALSE -- exporting a paid period
                                 silently returned it to 'exported'.

        It deliberately does NOT say the figures are frozen. `save`, `generate`
        and `adjustments` carry no status gate at all (pre-existing, and just
        as true of approved and exported periods), so a claim that the numbers
        cannot change would be false. Saying less and being right beats a
        reassuring sentence the app does not honour.

        If any of those three guards is ever relaxed, this copy has to change
        in the same PR.
      */}
      <ConfirmModal
        isOpen={showPaidConfirm}
        title="Mark this pay period as paid?"
        confirmLabel="Yes, mark as paid"
        confirmVariant="destructive"
        loading={actionLoading === 'mark-paid'}
        onConfirm={handleMarkPaid}
        onCancel={() => setShowPaidConfirm(false)}
        body={
          <>
            <p className="text-white font-medium">
              {formatDateRange(detail.period_start, detail.period_end)} — {money(totalPay)}{currency && ` ${currency}`}
              across {detail.timesheets.length} employee{detail.timesheets.length !== 1 ? 's' : ''}.
            </p>
            {/* The claim about the AMOUNTS is new, and it is now true.
                API #454 gated every writer into a paid period's figures — save,
                generate, adjustments (create and delete) and add-employee all
                answer 409 — joining the transitions, which were already closed
                (#446 reopen/delete, #447 both export writers). Before that this
                dialog could only honestly promise the STATUS would not move,
                which is why `does not overclaim` used to assert the opposite.

                The remedy sentence is load-bearing and was ASPIRATIONAL until
                API #450: a carry-forward adjustment for a worker with no ticket
                pay that period reached nothing at all. Verified end-to-end
                against production before this shipped. If that path regresses,
                this paragraph starts lying — see PayrollScreen.mark-paid.test. */}
            <p>
              This records that the money has gone out. <strong className={IRREVERSIBLE_WARNING_CLASS}>It cannot be undone.</strong>{' '}
              The amounts are locked too — a paid period can't be reopened, deleted,
              regenerated or edited, and nothing in FieldIQ will move it back to Approved
              or Exported.
            </p>
            <p className="text-fieldiq-muted">
              You'll still be able to view the period and download its CSV. If you find an
              error later, add the correction to the current open period and it will carry
              forward.
            </p>
          </>
        }
      />
    </>
  );
}

// ─── Main PayrollScreen ─────────────────────────────────────────────────────

export default function PayrollScreen() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const currency = useTenantCurrency();
  /*
   * ONE FLAG PER API GATE (fe#691). The delete control read
   * `role === 'admin' || role === 'super_admin'`, and `super_admin` is not in
   * DELETE /pay-periods/:id's list — that route is
   * `requireRole('admin', { strict: true })`, matched as written — so a
   * super_admin was shown a bin icon that answers 403.
   */
  const perms = usePayrollPermissions();
  const queryClient = useQueryClient();
  /**
   * WHICH PERIOD IS "CURRENT" IS THE SERVER'S CALL, NOT THIS SCREEN'S.
   *
   * The card used to take the first `status === 'open'` period off the list.
   * The list is sorted `period_start DESC`, so that is the open period furthest
   * in the FUTURE — on Smith, 2026-09-29, the card read Mar 1–15, 2027. The
   * API (#833) now answers `/pay-periods/current` with today taken in the
   * company's time zone, which is the one clock this screen does not have.
   *
   * ITS OWN KEY, not ClockInScreen's bare `['pay-period-current']`. This
   * screen also needs the envelope's `tenant_today` (the day the server
   * resolved "current" against), so it caches `{ period, tenantToday }`, and
   * two shapes under one key is what the shared key existed to prevent. The
   * key is a child of ClockIn's, so invalidating `['pay-period-current']`
   * still refreshes both.
   *
   * A 404 means the company has no current period, and the card is simply
   * absent. ANY OTHER FAILURE ALSO LEAVES THE CARD ABSENT, and deliberately
   * says nothing — the card has no "no current period" wording to fall back
   * to, so an unreachable server cannot be read as a tenant with no period.
   * A 404 is an answer, so it is not retried either.
   */
  const currentQuery = useQuery({
    queryKey: ['pay-period-current', 'with-tenant-today'],
    queryFn: () => api.payPeriods.current().then((r): CurrentPeriodAnswer => {
      const body = r.data;
      return {
        period: body && typeof body === 'object' && 'data' in body ? body.data ?? null : null,
        tenantToday: body?.tenant_today ?? null,
      };
    }),
    retry: (failureCount: number, err: any) =>
      err?.response?.status !== 404 && retryUnlessRefused()(failureCount, err),
    networkMode: 'always',
  });
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);
  const [error, setError] = useState('');
  /**
   * The LIST's read failing, kept apart from `error` above.
   *
   * `error` is an ACTION's refusal — an auto-create that was declined, a
   * delete that failed — and it belongs in a dismissible band. This one means
   * the register never arrived, and it has to take the place of the EMPTY
   * STATE rather than sit above it.
   *
   * THE OLD SHAPE WAS THE fe#573 BUG EXACTLY. One failed `listPayPeriods` left
   * `periods` as `[]`, so the screen rendered "No pay periods yet — Create
   * your first pay period to get started" with a Create button, under a
   * dismissible red strip. Dismiss the strip and what is left is a confident,
   * false claim that this tenant has no payroll history, with a WRITE offered
   * as the remedy for a failed READ. `GET /payroll/pay-periods` has no role
   * gate, so the realistic failure here is unreachable rather than refused —
   * which is precisely the case `networkMode` hides on the React Query
   * screens and the case a dead zone produces.
   */
  const [loadError, setLoadError] = useState<unknown>(null);
  const [errorIsConfigIssue, setErrorIsConfigIssue] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<PayPeriodFilterTab>('All');
  const [ppSortBy, setPpSortBy] = useState('period');
  const [ppSortDir, setPpSortDir] = useState<'asc' | 'desc'>('desc');
  // Inline employee previews per period
  const [periodDetails, setPeriodDetails] = useState<Record<string, PayPeriodDetail>>({});
  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    periodId: string;
    counts: Record<string, number>;
  } | null>(null);
  const [deleteForceLoading, setDeleteForceLoading] = useState(false);

  const loadPeriods = useCallback(async () => {
    try {
      const res = await api.payroll.listPayPeriods();
      const data: PayPeriod[] = res.data.data || [];
      setPeriods(data);
      setLoadError(null);
      // Load details for each period (employee previews).
      //
      // These 403 for every role outside admin / office_staff — the detail
      // route is gated and the list is not — so the swallow below is load
      // bearing and correct: the register itself must still render. What is
      // NOT correct is reading a missing detail as a zero, which is why the
      // current-period card now asks whether its detail arrived at all.
      const details: Record<string, PayPeriodDetail> = {};
      await Promise.all(data.slice(0, 10).map(async (p) => {
        try {
          const detailRes = await api.payroll.getPayPeriod(p.id);
          details[p.id] = detailRes.data.data;
        } catch {}
      }));
      setPeriodDetails(details);
      return data;
    } catch (err) { setLoadError(err); return []; } finally { setLoading(false); }
  }, []);

  // After anything that can move a period's status or create/delete one, the
  // server's idea of "current" can move too — reload both, never the list alone.
  const reloadPeriods = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pay-period-current'] });
    return loadPeriods();
  }, [queryClient, loadPeriods]);

  /**
   * Auto-create pay periods from the company's frequency settings, until one
   * covers today.
   *
   * "TODAY" IS THE SERVER'S DAY, AND ONLY THE SERVER'S. This used to gate on a
   * `todayStr` built here — the browser's clock read in the tenant's zone — and
   * on whether the loaded list covered it. That is a second answer to a
   * question the server already answers twice: `/pay-periods/current`
   * resolves today in the company's zone, and auto-create itself checks
   * coverage in the company's zone, under an advisory lock, and no-ops when
   * today is covered (#511, #833). No date derived in this browser decides
   * anything in this function.
   *
   * So the CALLER decides, from `/current` and the server's `tenant_today` it
   * carries (the effect below), and hands that day in. The loop stops as soon
   * as a created period reaches it, so it never ends on a call the server
   * would answer "no new period needed" — that answer writes an audit row,
   * and a page visit must not. A `data: null` still stops it.
   */
  const autoCreateIfNeeded = useCallback(async (tenantToday: string) => {
    setAutoCreating(true);
    try {
      // Create periods until we have one covering today
      let attempts = 0;
      const maxAttempts = 24; // safety limit (~1 year of semi-monthly)
      while (attempts < maxAttempts) {
        attempts++;
        const res = await api.payroll.autoCreatePeriod();
        // A 200 carrying `data: null` is the route's idempotent answer — it had
        // no new period to create — and is a SUCCESS. Dereferencing it threw a
        // TypeError with no err.response, which fell into the catch below with an
        // empty msg and reported "Failed to auto-create period" on a success.
        // Nothing was created and nothing more will be, so stop looping.
        const newPeriod = res.data?.data ?? null;
        if (!newPeriod) break;
        // Stop once this period reaches the server's today. An unreadable end
        // stops too: nothing proves another period is needed.
        const newEndStr = calendarDayString(newPeriod.period_end);
        if (!newEndStr || newEndStr >= tenantToday) break;
      }
      await reloadPeriods();
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || '';
      // The two config 400s are authored by POST /pay-periods/auto-create and
      // are the only messages it matches on. Everything else surfaces: the
      // overlap message belongs to the manual POST /pay-periods route, which
      // this screen never calls, so suppressing it here only hid real errors.
      if (status === 400 && (msg.toLowerCase().includes('no pay period configuration') || msg.toLowerCase().includes('inactive'))) {
        setError(msg);
        setErrorIsConfigIssue(true);
      } else {
        setError(msg || 'Failed to auto-create period');
        setErrorIsConfigIssue(false);
      }
      // Still reload to show whatever was created
      await reloadPeriods();
    } finally {
      setAutoCreating(false);
    }
  }, [reloadPeriods]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  /**
   * The mount's auto-create: ONLY WHEN NO PERIOD COVERS TODAY, in any status.
   * Once per mount, and only on a SETTLED `/current` answer
   * (`fetchStatus === 'idle'`), so a cached answer from an earlier visit
   * cannot decide while the refetch is out.
   *
   * "Today" is the `tenant_today` the server sent with that answer — never a
   * date made in this browser. No `tenant_today` (an API older than this
   * change): create nothing.
   *
   * A 200 is NOT "a period covers today". `/current` falls back to the latest
   * period that STARTED by today, whatever its status, so a tenant whose
   * schedule fell behind gets a period that has already ended. So:
   *
   * - `/current`'s period ends on or after today → covered (open, approved,
   *   exported or paid alike) → nothing.
   * - any loaded period ends on or after today → covered, or a later period
   *   already exists past a gap, which auto-create would extend rather than
   *   fill → nothing. This is the old list gate, now on the server's day.
   * - otherwise (a 404, or a 200 whose period ended) → run the loop.
   * - a 409, any other failure, or a list that failed to load → nothing. A
   *   write is not the remedy for a read nobody could answer.
   *
   * The point is that a page visit sends auto-create only when it will
   * create: the server writes an audit row even for its "no new period
   * needed" answer, and a visit must not leave one.
   *
   * ONLY FOR A ROLE AUTO-CREATE ADMITS (`requireRole('admin',
   * 'office_staff')`; `/current` is open to every role). While the
   * elevated-dispatcher flag loads, `canEditPeriod` reads false, so this waits
   * rather than marking the mount done.
   */
  const autoCreateTried = useRef(false);
  useEffect(() => {
    if (autoCreateTried.current || !perms.canEditPeriod) return;
    if (currentQuery.fetchStatus !== 'idle' || loading) return;

    let tenantToday: string | null = null;
    let currentEnd: string | null = null;
    if (currentQuery.isSuccess) {
      tenantToday = currentQuery.data.tenantToday;
      currentEnd = calendarDayString(currentQuery.data.period?.period_end);
    } else if (currentQuery.isError) {
      const response = (currentQuery.error as any)?.response;
      if (response?.status !== 404) { autoCreateTried.current = true; return; }
      tenantToday = response?.data?.tenant_today ?? null;
    } else {
      return;
    }

    autoCreateTried.current = true;
    if (!tenantToday || loadError) return;
    if (currentEnd && currentEnd >= tenantToday) return;
    const reachesToday = periods.some((p) => {
      const end = calendarDayString(p.period_end);
      return end !== null && end >= tenantToday!;
    });
    if (reachesToday) return;
    autoCreateIfNeeded(tenantToday);
  }, [perms.canEditPeriod, currentQuery.fetchStatus, currentQuery.isSuccess, currentQuery.isError,
      currentQuery.data, currentQuery.error, loading, loadError, periods, autoCreateIfNeeded]);

  const handleAutoCreate = async () => {
    setAutoCreating(true); setError(''); setErrorIsConfigIssue(false);
    try {
      const res = await api.payroll.autoCreatePeriod();
      await reloadPeriods();
      // Same null-data success as autoCreateIfNeeded, reached by the button
      // instead of the mount effect. This one has a user gesture behind it, so
      // say why nothing appeared rather than leaving the click looking ignored.
      const created = res.data?.data ?? null;
      if (created) {
        setSelectedPeriodId(created.id);
      } else {
        toast.success(res.data?.message || 'No new period needed — the next period already exists.');
      }
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || 'Failed to auto-create period';
      setError(msg);
      if (status === 400 && (msg.toLowerCase().includes('no pay period configuration') || msg.toLowerCase().includes('inactive'))) {
        setErrorIsConfigIssue(true);
      } else {
        setErrorIsConfigIssue(false);
      }
    } finally { setAutoCreating(false); }
  };

  const handleDeletePeriod = async (e: React.MouseEvent, periodId: string) => {
    e.stopPropagation();
    try {
      await api.payPeriodConfig.deletePeriod(periodId);
      await reloadPeriods();
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error('Only admins can delete pay periods.');
        return;
      }
      if (status === 409) {
        const counts: Record<string, number> = err.response?.data?.counts || {};
        setDeleteConfirm({ periodId, counts });
        return;
      }
      setError(err.response?.data?.error || 'Failed to delete period');
    }
  };

  const handleForceDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteForceLoading(true);
    try {
      await api.payPeriodConfig.deletePeriod(deleteConfirm.periodId, { force: true });
      setDeleteConfirm(null);
      await reloadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete period.');
      setDeleteConfirm(null);
    } finally {
      setDeleteForceLoading(false);
    }
  };

  // Detail view
  if (selectedPeriodId) {
    return (
      <div data-testid="pay-period-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <PayPeriodDetailView periodId={selectedPeriodId} onBack={() => { setSelectedPeriodId(null); reloadPeriods(); }} />
      </div>
    );
  }

  // Computed values for current period & filtering
  // Match the server's current period to its LIST row by id, so the card's
  // money and headcount are the same figures as the register row below it.
  // The /current row is the fallback only when the list does not hold it.
  const currentFromServer: PayPeriod | null = currentQuery.data?.period?.id ? currentQuery.data.period : null;
  const currentPeriod = currentFromServer
    ? periods.find(p => p.id === currentFromServer.id) ?? currentFromServer
    : null;
  const currentDetail = currentPeriod ? periodDetails[currentPeriod.id] : null;
  /*
   * ABSENT, NOT ZERO — and here the absence has two quite different causes,
   * neither of which is "nobody is on this period".
   *
   *   1. the detail route is admin / office_staff, and the LIST is ungated, so
   *      a supervisor or a plain dispatcher gets a 403 for every one of these
   *      previews. The old `|| 0` turned that refusal into "0 active, 0
   *      approved" — a confident claim about a period the screen was not
   *      allowed to look inside. That is AlertsScreen's "All Clear" in
   *      miniature.
   *   2. only the first TEN periods are prefetched, so an open period further
   *      down the list has no detail either.
   *
   * `null` in both cases, and displayValue() renders it as a dash. The
   * employee COUNT beside it does not need the detail at all — the list's own
   * `employee_count` is a live COUNT(DISTINCT user_id) computed by the
   * endpoint and is never null — so that figure stays a number while the split
   * that genuinely did not arrive reads as absent.
   */
  const currentSplit = currentDetail?.timesheets
    ? {
        active: currentDetail.timesheets.filter(t => t.status !== 'approved').length,
        approved: currentDetail.timesheets.filter(t => t.status === 'approved').length,
      }
    : null;

  // 'Paid' is a real, reachable status as of API #446 and needs its own tab --
  // without one a paid period is findable only under 'All', which reads as the
  // period having gone missing rather than having been closed out.
  const filterTabs = PAY_PERIOD_FILTER_TABS;
  const tabCounts = {
    All: periods.length,
    Open: periods.filter(p => p.status === 'open').length,
    Approved: periods.filter(p => p.status === 'approved').length,
    Exported: periods.filter(p => p.status === 'exported').length,
    Paid: periods.filter(p => p.status === 'paid').length,
  };

  const filteredPeriods = periods.filter(pp => {
    if (filterTab !== 'All' && pp.status !== filterTab.toLowerCase()) return false;
    if (search) {
      const q = search.toLowerCase();
      const label = formatDateRange(pp.period_start, pp.period_end).toLowerCase();
      if (!label.includes(q)) return false;
    }
    return true;
  });

  /**
   * The band's four figures — tallies of the ONE list this screen already
   * loads, with no added query.
   *
   * WHICH LIST, AND WHY EVERY CHIP GETS A REAL COUNT. `GET /payroll/pay-periods`
   * has no LIMIT, no OFFSET and no pagination: it returns every live period
   * for the tenant in one response. So unlike /expenses — where the server
   * filters by status and an unselected chip's rows were never fetched — every
   * status is in hand here and every chip can carry a true count. The band
   * describes the FILTER IN VIEW; the chips describe the whole list.
   *
   * `null` while the list is in flight and whenever it did not arrive, so a
   * failed read shows four dashes rather than four zeros.
   *
   * THE MONEY FIGURES ARE ALL-OR-NOTHING. `sumMoney` returns null the moment
   * any contributor is absent, because a payroll total that silently skipped a
   * period is an understatement that looks exactly like a correct figure.
   * (`total_gross_pay` is COALESCE'd to 0 by the endpoint today and so is never
   * absent in practice — the rule is kept anyway, because that is a fact about
   * the current SQL and not a contract, and the failure it guards against is
   * invisible.)
   */
  const tally = loading || loadError
    ? null
    : {
        periods: filteredPeriods.length,
        gross: sumMoney(filteredPeriods.map(p => p.total_gross_pay)),
        open: filteredPeriods.filter(p => p.status === 'open').length,
        // What is signed off but has not gone out yet — the figure the office
        // is actually chasing on this screen. `paid` is excluded because the
        // money has left, and `open` because it is not yet a commitment.
        awaiting: sumMoney(
          filteredPeriods
            .filter(p => p.status === 'approved' || p.status === 'exported')
            .map(p => p.total_gross_pay),
        ),
      };

  const togglePpSort = makeToggleSort(setPpSortBy, setPpSortDir, ppSortBy);
  const sortedPeriods = [...filteredPeriods].sort((a, b) => {
    let aVal: any, bVal: any;
    switch (ppSortBy) {
      case 'period': aVal = a.period_start || ''; bVal = b.period_start || ''; break;
      case 'employees': aVal = a.employee_count || periodDetails[a.id]?.timesheets?.length || 0; bVal = b.employee_count || periodDetails[b.id]?.timesheets?.length || 0; break;
      // parseMoney, not `|| 0`: sortCompare already sorts null LAST, so a period
      // whose gross pay the server has not computed sinks to the bottom instead
      // of sorting in among the genuinely-zero ones as if it were $0.
      case 'total_payroll': aVal = parseMoney(a.total_gross_pay); bVal = parseMoney(b.total_gross_pay); break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      case 'approved': aVal = a.approved_at || ''; bVal = b.approved_at || ''; break;
      case 'exported': aVal = a.exported_at || ''; bVal = b.exported_at || ''; break;
      default: return 0;
    }
    return sortCompare(aVal, bVal, ppSortDir);
  });

  // List view
  return (
    <div data-testid="payroll-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Payroll cycle"
        title="Payroll"
        onBack={() => navigate(-1)}
        backLabel="Back"
        /* The four figures describe the FILTER IN VIEW — see `tally` for why
           they are tallies of the one loaded list, and why a money figure is
           all-or-nothing. The third label is 'Open' (well under the ~70px the
           hero circle's clearance was measured for); 'Awaiting pay' sits in
           the fourth column, which the circle's edge does not cross. */
        stats={[
          {
            icon: <CalendarDays className="size-4" aria-hidden="true" />,
            label: 'Periods',
            sub: 'In the filter shown',
            tone: 'accent',
            loading,
            value: displayValue(tally?.periods),
          },
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: 'Gross pay',
            sub: 'Periods shown',
            tone: 'info',
            loading,
            value: fmtCurrency(tally?.gross),
          },
          {
            icon: <FolderOpen className="size-4" aria-hidden="true" />,
            label: 'Open',
            sub: 'Still being built',
            tone: 'neutral',
            loading,
            value: displayValue(tally?.open),
          },
          {
            icon: <HandCoins className="size-4" aria-hidden="true" />,
            label: 'Awaiting pay',
            sub: 'Approved, not yet paid',
            tone: 'success',
            loading,
            value: fmtCurrency(tally?.awaiting),
          },
        ]}
        /* This row is the way in to /payroll/summary and /payroll/workers. Both are
           routed and neither is in the nav: Summary because it overlaps Payroll
           Reports and is period-scoped like this screen, Workers because a sidebar
           entry cannot be gated by role and GET /pay-profiles refuses supervisor
           and logistics — two of the five roles that reach this tree. A top-level
           item that dead-ends two-fifths of its audience is poor nav, so the way in
           is here, on the screen that owns the same object.

           The three navigation buttons take a white/25 border and NO colour
           class: they inherit the band's near-white ink. A plain white-text
           utility would be remapped to the heading colour in light mode. */
        actions={
          <>
            {perms.canEditPeriod && (
              <button onClick={() => setShowNewModal(true)}
                className="fi-action fi-action--compact fi-action--primary">
                <Plus size={14} /> New Period
              </button>
            )}
            {/* Was /payroll/timesheets, which is not a registered route — so it hit the
                catch-all and redirected to Home. Not a dead button: an unexplained
                ejection out of Payroll, with nothing on screen to say what happened.
                /payroll/approvals IS the timesheet list this was reaching for. */}
            <button onClick={() => navigate('/payroll/approvals')}
              className="fi-action fi-action--compact border border-white/25">
              <ClipboardCheck size={14} /> Timesheets
            </button>
            <button onClick={() => navigate('/payroll/summary')}
              className="fi-action fi-action--compact border border-white/25">
              <DollarSign size={14} /> Summary
            </button>
            <button onClick={() => navigate('/payroll/workers')}
              className="fi-action fi-action--compact border border-white/25">
              <Users size={14} /> Workers
            </button>
          </>
        }
      />

      {error && (
        <div className="max-w-[1920px] mx-auto px-[var(--fi-page-inline)] mt-3">
          <div className="text-sm text-fieldiq-danger bg-fieldiq-danger/10 border border-fieldiq-danger/30 rounded-fi-panel p-3 flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <span>{error}</span>
              {/* PUT /pay-periods/config is requireRole('admin', {strict:true})
                  — api#736 put overtime rules and pay codes behind the same
                  gate. Office staff can trip this 400 (auto-create is theirs)
                  and cannot fix it, so they are told who can rather than sent
                  to a page whose Save answers 403. */}
              {errorIsConfigIssue && (perms.canConfigurePayPeriods ? (
                <button
                  onClick={() => navigate('/settings/pay-periods')}
                  className="fi-action fi-action--compact fi-action--primary whitespace-nowrap"
                >
                  Configure Pay Periods
                </button>
              ) : (
                <span className="text-xs text-fieldiq-muted whitespace-nowrap">
                  An admin has to set this up in Settings → Pay Periods.
                </span>
              ))}
            </div>
            <button onClick={() => { setError(''); setErrorIsConfigIssue(false); }} aria-label="Dismiss"><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Auto-creating banner */}
      {autoCreating && !loading && (
        <div className="max-w-[1920px] mx-auto px-[var(--fi-page-inline)] mt-3">
          <div className="bg-fi-amber/10 border border-fi-amber/30 rounded-fi-panel p-3 flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-fi-signal-amber" />
            <span className="text-sm text-fi-signal-amber font-medium">Creating pay periods from your settings...</span>
          </div>
        </div>
      )}

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {loading || (autoCreating && periods.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={24} className="animate-spin text-fieldiq-muted" />
            <p className="text-sm text-fieldiq-text">{autoCreating ? 'Setting up pay periods...' : 'Loading payroll...'}</p>
          </div>
        ) : loadError ? (
          /*
           * THE THIRD BRANCH, and it comes BEFORE the empty state rather than
           * as a banner above it. `periods` is `[]` after a failed read, so
           * the branch below would otherwise render "No pay periods yet" and
           * offer Create First Period — a WRITE as the remedy for a failed
           * READ, on a screen where creating a duplicate period is a real
           * consequence (fe#573, and the reason lib/load-failure.ts exists).
           */
          <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
            <LoadFailureNotice error={loadError} subject="pay periods" />
          </section>
        ) : periods.length === 0 ? (
          <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
            <div className="px-4 py-16 text-center">
              <CalendarDays size={40} className="mx-auto mb-3 text-fieldiq-muted" />
              <p className="text-fieldiq-text font-semibold">No pay periods yet</p>
              {/* The list really did arrive and really is empty, so this says
                  so as a fact about the data — and only offers the remedy to
                  the roles POST /auto-create admits. */}
              <p className="text-sm text-fieldiq-muted mt-1 mb-4">
                {perms.canEditPeriod
                  ? 'Create your first pay period to get started.'
                  : 'An admin or office staff sets the first pay period up.'}
              </p>
              {perms.canEditPeriod && (
                <button onClick={handleAutoCreate} disabled={autoCreating}
                  className="fi-action fi-action--standard fi-action--primary mx-auto disabled:opacity-50">
                  {autoCreating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  Create First Period
                </button>
              )}
            </div>
          </section>
        ) : (
          <>
            {/* Current Period Summary Card */}
            {currentPeriod && (
              <button type="button"
                className="w-full text-left rounded-fi-panel border border-fi-amber/25 bg-fieldiq-card p-4 transition-colors hover:border-fi-amber/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-fi-amber"
                onClick={() => setSelectedPeriodId(currentPeriod.id)}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign size={16} className="text-fi-signal-amber" />
                      <span className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Current Period</span>
                    </div>
                    <p className="text-sm font-semibold text-fieldiq-text">{formatDateRange(currentPeriod.period_start, currentPeriod.period_end)}</p>
                    <p className="mt-1 text-2xl font-extrabold tabular-nums text-fieldiq-text">
                      {money(currentPeriod.total_gross_pay)}{currency && <> <span className="text-sm font-normal text-fieldiq-muted">{currency}</span></>}
                    </p>
                  </div>
                  <div className="space-y-1 text-right">
                    {/* `employee_count` is the endpoint's own live
                        COUNT(DISTINCT user_id) and is never null, so it is a
                        number. The split beside it comes from the detail
                        prefetch, which is gated and capped at ten — see
                        `currentSplit`; when it did not arrive it reads as a
                        dash, never as zero. */}
                    <div className="text-sm text-fieldiq-muted">{displayValue(currentPeriod.employee_count)} employees</div>
                    <div className="text-xs text-fi-signal-blue">{displayValue(currentSplit?.active)} active</div>
                    <div className="text-xs text-fi-signal-green">{displayValue(currentSplit?.approved)} approved</div>
                  </div>
                </div>
              </button>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search pay periods..."
                aria-label="Search pay periods"
                className="w-full h-10 rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-10 pr-9 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fi-amber/50"
              />
              {search && (
                <button onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X size={14} className="text-fieldiq-muted" />
                </button>
              )}
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
              {filterTabs.map(tab => (
                <button
                  key={tab}
                  type="button"
                  aria-pressed={filterTab === tab}
                  onClick={() => setFilterTab(tab)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    filterTab === tab
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {tab}
                  <span className={`tabular-nums ${filterTab === tab ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                    {displayValue(tabCounts[tab])}
                  </span>
                </button>
              ))}
            </div>

            {/* The register. The amber edge is on this panel and nowhere else;
                rows carry a status rail, never a second amber one. */}
            <section
              data-testid="pay-period-register"
              aria-labelledby="pay-period-register-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Pay period register</p>
                <h2 id="pay-period-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                  {filterTab === 'All' ? 'All pay periods' : `${filterTab} pay periods`}
                </h2>
              </header>

              {sortedPeriods.length === 0 ? (
                <div className="px-4 py-16 text-center">
                  <Search size={40} className="mx-auto mb-3 text-fieldiq-muted" />
                  <p className="text-fieldiq-text font-semibold">No pay periods found</p>
                  <p className="mt-1 text-sm text-fieldiq-muted">
                    {search ? 'Nothing matches that search.' : `No periods are ${filterTab.toLowerCase()}.`}
                  </p>
                </div>
              ) : (
                /* A min width rather than a card stack below md. The old stack
                   was a SECOND copy of every row — and it had drifted: its
                   status palette was a third hand-rolled one with no 'exported'
                   and no 'paid' case, so a PAID period rendered grey and
                   identical to an exported one. It also read `filteredPeriods`
                   where the table read `sortedPeriods`, so the two disagreed
                   about order. The rows scroll sideways instead. */
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] text-left text-sm">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      {/* Transparent 4px so the head lines up with the railed rows. */}
                      <tr className="border-l-4 border-l-transparent">
                        <SortTh label="Period" field="period" sortBy={ppSortBy} sortDir={ppSortDir} onSort={togglePpSort} className="min-w-[160px]" />
                        <SortTh label="Employees" field="employees" sortBy={ppSortBy} sortDir={ppSortDir} onSort={togglePpSort} />
                        <SortTh label="Total Payroll" field="total_payroll" sortBy={ppSortBy} sortDir={ppSortDir} onSort={togglePpSort} className="!text-right" />
                        <SortTh label="Status" field="status" sortBy={ppSortBy} sortDir={ppSortDir} onSort={togglePpSort} />
                        <SortTh label="Approved" field="approved" sortBy={ppSortBy} sortDir={ppSortDir} onSort={togglePpSort} />
                        <SortTh label="Exported" field="exported" sortBy={ppSortBy} sortDir={ppSortDir} onSort={togglePpSort} />
                        <th scope="col" className="w-20 px-4 py-3 text-right"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPeriods.map(pp => {
                        const skin = periodSkin(pp.status);
                        const open = () => setSelectedPeriodId(pp.id);
                        /*
                         * THE fe#647 EXCEPTION. This row holds an inline
                         * Delete button, so it must NOT take role="button":
                         * that makes its children presentational and the bin
                         * stops being announced, and a row-level Enter handler
                         * that preventDefaults would swallow Delete's own
                         * activation. So: tabIndex and a click, no role, and
                         * Enter/Space only when the ROW itself has focus.
                         */
                        const rowKeys = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); }
                        };
                        return (
                          <tr
                            key={pp.id}
                            data-testid="pay-period-row"
                            tabIndex={0}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skin.rail}`}
                            onClick={open}
                            onKeyDown={rowKeys}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Calendar size={16} className="shrink-0 text-fieldiq-muted" />
                                <span className="font-semibold text-fieldiq-text">{formatDateRange(pp.period_start, pp.period_end)}</span>
                              </div>
                            </td>
                            {/* The endpoint computes this as a live
                                COUNT(DISTINCT user_id) and COALESCEs it, so it
                                is a number on every row. It used to read
                                `pp.employee_count || detail?.timesheets?.length || 0`,
                                whose `||` sent a REAL zero on to the next term
                                — and the term after that was a literal 0. */}
                            <td className="px-4 py-3 tabular-nums text-fieldiq-text">{displayValue(pp.employee_count)}</td>
                            <td className="px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap text-fieldiq-text">{money(pp.total_gross_pay)}{currency && ` ${currency}`}</td>
                            <td className="px-4 py-3"><StatusBadge status={pp.status} /></td>
                            <td className="px-4 py-3 whitespace-nowrap text-fieldiq-muted">{pp.approved_at ? formatDateFull(pp.approved_at, timeZone) : '—'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-fieldiq-muted">{pp.exported_at ? formatDateFull(pp.exported_at, timeZone) : '—'}</td>
                            {/* The action cell stops click propagation, or the
                                bin would also open the period behind its own
                                confirmation. */}
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1">
                                {pp.status === 'open' && perms.canDeletePeriod && (
                                  <button onClick={(e) => handleDeletePeriod(e, pp.id)}
                                    aria-label={`Delete the ${formatDateRange(pp.period_start, pp.period_end)} pay period`}
                                    className="rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-fi-signal-red">
                                    <Trash2 size={14} />
                                  </button>
                                )}
                                <ChevronRight size={16} className="text-fieldiq-muted" aria-hidden="true" />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <footer className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                Showing {filteredPeriods.length} of {periods.length} periods
              </footer>
            </section>
          </>
        )}
        </div>
      </section>

      {showNewModal && <NewPeriodModal onClose={() => setShowNewModal(false)} onCreated={reloadPeriods} />}

      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete pay period with data?"
        body={
          deleteConfirm ? (
            <div className="space-y-2">
              <p>This period has attached data that will be permanently deleted:</p>
              <ul className="list-disc list-inside text-gray-400">
                {Object.entries(deleteConfirm.counts)
                  .filter(([, count]) => count > 0)
                  .map(([table, count]) => (
                    <li key={table}>{count} {table.replace(/_/g, ' ')}</li>
                  ))}
              </ul>
              <p className={IRREVERSIBLE_WARNING_CLASS}>This action cannot be undone.</p>
            </div>
          ) : null
        }
        confirmLabel="Delete permanently"
        confirmVariant="destructive"
        loading={deleteForceLoading}
        onConfirm={handleForceDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
